// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// -- Two applications in one file ---------------------------------------------
//
// The routes below fall into two groups that share nothing but a bundle:
//
//   /customer-portal/*   the COMPANY's screens. Ordinary ERP pages, behind the
//                        shell's session and its permission guard.
//   /portal/*            the CUSTOMER's. Declared `public` so the shell's guard
//                        lets them past - they have their own credential and
//                        their own guard, `requirePortal` below - and rendered
//                        with `layout: 'auth'`, which is the shell's way of
//                        saying "this screen brings its own frame".
//
// That second group is why this module needs no change to platform/web at all:
// the portal is a different application for a different person, and it is built
// out of what a module is already allowed to contribute.

/**
 * The portal's own route guard.
 *
 * The shell's guard skips anything marked `public`, which is what lets these
 * routes exist at all - so the check has to happen here. It resolves the
 * session once and sends anybody without one to the sign-in screen, remembering
 * where they were going.
 *
 * The two imports are dynamic, and that is not a performance trick: this file
 * is read by the repository's i18n test with plain Node, which has no bundler
 * and no browser. A static import of anything that touches `localStorage` would
 * make the whole catalogue unreadable to the one test that checks it is
 * complete. The same reasoning keeps the stylesheet out of here and inside the
 * components that draw with it.
 */
async function requirePortal(to) {
  const { getToken } = await import('./api/portal.js')
  if (!getToken()) return { path: '/portal/login', query: { redirect: to.fullPath } }

  const { portal, load } = await import('./api/session.js')
  if (!portal.ready && !(await load())) {
    return { path: '/portal/login', query: { redirect: to.fullPath } }
  }
  return true
}

// Plain data, so a static import is safe here (see above). With the extension:
// Node's loader needs it.
import assistantMessages from './assistantMessages.js'

export const routes = [
  // -- The company's screens -------------------------------------------------
  {
    path: '/customer-portal',
    name: 'customer-portal',
    component: () => import('./pages/PortalOverviewPage.vue'),
    meta: { permission: 'portal:read', title: 'customerPortal.title' },
  },
  {
    path: '/customer-portal/accounts',
    name: 'customer-portal-accounts',
    component: () => import('./pages/PortalAccountsPage.vue'),
    meta: { permission: 'portal:accounts', title: 'customerPortal.accounts.title' },
  },
  {
    path: '/customer-portal/publications',
    name: 'customer-portal-publications',
    component: () => import('./pages/PortalPublicationsPage.vue'),
    meta: { permission: 'portal:read', title: 'customerPortal.publications.title' },
  },
  {
    path: '/customer-portal/requests',
    name: 'customer-portal-requests',
    component: () => import('./pages/PortalInboxPage.vue'),
    meta: { permission: 'portal:read', title: 'customerPortal.inbox.title' },
  },
  {
    path: '/customer-portal/requests/:id',
    name: 'customer-portal-request',
    component: () => import('./pages/PortalRequestDetailPage.vue'),
    meta: { permission: 'portal:read', title: 'customerPortal.inbox.title' },
  },
  {
    path: '/customer-portal/settings',
    name: 'customer-portal-settings',
    component: () => import('./pages/PortalSettingsPage.vue'),
    meta: { permission: 'portal:configure', title: 'customerPortal.settings.title' },
  },

  // -- The customer's portal -------------------------------------------------
  //
  // `public: true` gets them past the shell's guard; `layout: 'auth'` renders
  // them bare, without the ERP's sidebar and top bar. Neither is a trick: both
  // are the shell's own vocabulary for "a screen that is not part of the ERP
  // chrome", which is exactly what these are.
  {
    path: '/portal/login',
    name: 'portal-login',
    component: () => import('./pages/portal/PortalLoginPage.vue'),
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal/welcome',
    name: 'portal-welcome',
    component: () => import('./pages/portal/PortalWelcomePage.vue'),
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal',
    name: 'portal-home',
    component: () => import('./pages/portal/PortalHomePage.vue'),
    beforeEnter: requirePortal,
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal/section/:section',
    name: 'portal-section',
    component: () => import('./pages/portal/PortalSectionPage.vue'),
    beforeEnter: requirePortal,
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal/item/:id',
    name: 'portal-item',
    component: () => import('./pages/portal/PortalItemPage.vue'),
    beforeEnter: requirePortal,
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal/requests',
    name: 'portal-requests',
    component: () => import('./pages/portal/PortalRequestsPage.vue'),
    beforeEnter: requirePortal,
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal/requests/new',
    name: 'portal-request-new',
    component: () => import('./pages/portal/PortalNewRequestPage.vue'),
    beforeEnter: requirePortal,
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal/requests/:id',
    name: 'portal-request',
    component: () => import('./pages/portal/PortalRequestPage.vue'),
    beforeEnter: requirePortal,
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
  {
    path: '/portal/profile',
    name: 'portal-profile',
    component: () => import('./pages/portal/PortalProfilePage.vue'),
    beforeEnter: requirePortal,
    meta: { public: true, layout: 'auth', title: 'portal.title' },
  },
]

/**
 * One row with children, because the module is four screens over one subject.
 * The shell filters each by permission before drawing it, so somebody who can
 * only answer requests sees one child and not four.
 *
 * The portal's own routes contribute NO navigation: they are not part of this
 * ERP's menu, and an entry pointing at them would be an entry that signs the
 * employee out of nothing and into a customer's screen.
 */
export const navigation = [
  {
    to: '/customer-portal',
    labelKey: 'customerPortal.title',
    permission: 'portal:read',
    icon: 'door',
    group: 'business',
    children: [
      { to: '/customer-portal/requests', labelKey: 'customerPortal.nav.inbox', permission: 'portal:read' },
      { to: '/customer-portal/publications', labelKey: 'customerPortal.nav.publications', permission: 'portal:read' },
      { to: '/customer-portal/accounts', labelKey: 'customerPortal.nav.accounts', permission: 'portal:accounts' },
      { to: '/customer-portal/settings', labelKey: 'customerPortal.nav.settings', permission: 'portal:configure' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (section 16): a module that ships two is a module that cannot be
// sold to the third client. That includes what this module is called on the
// PLATFORM's own screens - Roles, Settings, Audit and the assistant panel -
// because a module that makes somebody add keys to the shell is not extractable.
//
// The customer's half lives under `portal.*` and the company's under
// `customerPortal.*`, and the split is not cosmetic: the first is read by
// somebody who has never seen this ERP and must never contain a word of its
// vocabulary.
export const messages = {
  es: {
    errors: {
      PORTAL_ROLE_TOO_WIDE: 'No puedes dar ni quitar más permisos de los que tienes.',
    },
    portal: {
      title: "Portal de cliente",
      menu: "Menú",
      signOut: "Salir",
      saved: "Guardado",
      search: {
        open: "Buscar",
        title: "Buscar en tu portal",
        placeholder: "Referencia, máquina, obra, factura…",
        none: "No hay nada con eso",
        go: "Buscar",
        hint: "Escribe dos letras o más y busca en todo lo que puedes ver.",
        keepTyping: "Sigue escribiendo…",
      },
      sections: {
        home: "Inicio",
        requests: "Solicitudes",
        approvals: "Aprobaciones",
        projects: "Proyectos",
        orders: "Pedidos",
        assets: "Equipos",
        support: "Soporte",
        maintenance: "Mantenimiento",
        documents: "Documentos",
        quotes: "Presupuestos",
        invoices: "Facturas",
        contracts: "Contratos",
        meetings: "Reuniones",
        profile: "Mi empresa",
      },
      requestTypes: {
        support: "Avería o incidencia",
        quote: "Pedir presupuesto",
        maintenance: "Pedir mantenimiento",
        part: "Pedir un recambio",
        document: "Pedir documentación",
        meeting: "Pedir una reunión",
      },
      roles: {
        manager: "Responsable",
        operations: "Operaciones",
        accounts: "Administración",
      },
      fields: {
        email: "Correo",
        password: "Contraseña",
        newPassword: "Contraseña nueva",
        repeatPassword: "Repite la contraseña",
        currentPassword: "Contraseña actual",
        name: "Nombre",
        jobTitle: "Cargo",
        subject: "Asunto",
        description: "Cuéntanos qué pasa",
        site: "Centro",
        anySite: "Cualquiera",
        about: "Sobre qué",
        nothingInParticular: "Nada en concreto",
        symptom: "Qué hace la máquina",
        since: "Desde cuándo",
        stopped: "La línea está parada",
        need: "Qué necesitas",
        deadline: "Para cuándo",
        preferred: "Fecha que os va bien",
        part: "Referencia o descripción",
        quantity: "Cantidad",
        attendees: "Quién asistirá",
        aboutHint: "Así sabemos de qué hablas sin tener que preguntártelo.",
      },
      signIn: {
        title: "Entra en tu portal",
        subtitle: "Aquí tienes lo tuyo: lo que está en marcha, lo que espera tu respuesta y lo que puedes pedir.",
        emailPlaceholder: "tu correo",
        go: "Entrar",
        forgot: "No recuerdo la contraseña",
        forgotTitle: "Recuperar el acceso",
        forgotHelp: "Escribe tu correo y te enviamos un enlace para poner una contraseña nueva.",
        forgotGo: "Enviar enlace",
        forgotSent: "Si esa dirección tiene acceso, el enlace ya va de camino.",
        forgotSentHint: "Si esa dirección tiene acceso, el enlace llega en unos minutos y caduca.",
      },
      welcome: {
        title: "Elige tu contraseña",
        subtitle: "Es el último paso: la eliges tú y nadie más la ve.",
        go: "Entrar",
        mismatch: "Las dos contraseñas no coinciden",
        noToken: "Este enlace no lleva invitación. Pide otro a tu proveedor.",
        passwordHint: "Ocho caracteres o más.",
        noTokenHint: "Abre el enlace del correo tal cual: es de un solo uso y caduca.",
        goToSignIn: "Ir a la entrada",
      },
      home: {
        pending: "Pendiente de ti",
        nothingPending: "No hay nada esperando tu respuesta",
        inProgress: "En curso",
        upcoming: "Próximo",
        activity: "Lo último",
        ask: "¿Qué necesitas?",
        summarise: "Resúmeme cómo va todo",
        goHome: "Volver al inicio",
        greeting: "Hola, {name}",
        greetingAnon: "Hola",
        nothingPendingHint: "No hay nada esperando tu respuesta.",
        summaryTitle: "Cómo va",
        brandNew: "Aquí todavía no hay nada",
        brandNewHint: "En cuanto haya algo que enseñarte, aparecerá en esta pantalla. Mientras tanto, puedes escribirnos desde aquí.",
      },
      activity: {
        published: "Nuevo: {what}",
        replied: "{what} ha respondido",
        wrote: "{what} escribió",
      },
      card: {
        waitingOnYou: "Espera tu respuesta",
        on: "{date}",
        due: "Para el {date}",
      },
      decisionState: {
        pending: "Pendiente",
        approved: "Aprobado",
        rejected: "Rechazado",
        changes: "Cambios pedidos",
        superseded: "Ya no cuenta: hay una versión nueva",
        cancelled: "Anulado",
      },
      section: {
        ask: "Pedir algo",
        filter: "Buscar aquí",
        none: "Todavía no hay nada en {what}",
        noneFiltered: "No hay nada que coincida",
        clear: "Quitar el filtro",
        count: "1 elemento | {n} elementos",
        noneHint: "Cuando haya algo que enseñarte en esta sección, aparecerá aquí.",
        noneFilteredHint: "Prueba con otras palabras.",
      },
      pager: {
        previous: "Anterior",
        next: "Siguiente",
        of: "Página {page} de {pages}",
      },
      item: {
        back: "Volver",
        status: "Estado",
        amount: "Importe",
        date: "Fecha",
        dueDate: "Fecha prevista",
        decisionAsked: "Esto espera tu respuesta (versión {version})",
        commentPlaceholder: "Si quieres, cuéntanos por qué",
        approve: "Aprobar",
        changes: "Pedir cambios",
        reject: "Rechazar",
        decisionSent: "Enviado. Ya lo tienen.",
        explain: "Explícamelo en dos líneas",
        description: "Concepto",
        quantity: "Cantidad",
        unitPrice: "Precio",
        total: "Total",
        files: "Documentos",
        decisions: "Respuestas dadas",
        versionIs: "· versión {version}",
        byWho: "· {who}",
        version: "Versión",
        lines: "Detalle",
        progress: "Avance",
        progressIs: "{percent}% hecho",
        comment: "Comentario",
        commentHint: "Opcional, y lo lee quien te lo ha mandado.",
        decidedOn: "Respondido el {date}",
      },
      thread: {
        title: "Conversación",
        empty: "Todavía no ha escrito nadie",
        placeholder: "Escribe aquí",
        send: "Enviar",
        attach: "Adjuntar",
        chosen: "{count} archivo(s) listos para enviar",
        removeFile: "Quitar el fichero",
      },
      requests: {
        new: "Nueva solicitud",
        newTitle: "¿Qué necesitas?",
        none: "Todavía no has pedido nada",
        whatKind: "¿De qué se trata?",
        sayIt: "Dilo como quieras",
        sayItPlaceholder: "La máquina hace un ruido raro cuando sube el eje…",
        tidyUp: "Ordénamelo",
        stillMissing: "Con esto sabrían más:",
        send: "Enviar la solicitud",
        sent: "Enviada. Su referencia es {code}.",
        tabs: {
          open: "Abiertas",
          all: "Todas",
        },
        newSubtitle: "Cuéntanos qué necesitas y quién lo lleva te contestará por aquí.",
        details: "Los detalles",
        noneOpen: "No tienes nada abierto",
        noneHint: "Lo que nos pidas desde aquí queda registrado, con su número y su respuesta.",
        noTypes: "No se puede pedir nada por ahora",
        noTypesHint: "Habla con tu contacto habitual.",
        leaveTitle: "¿Dejar esto a medias?",
        leaveBody: "Lo que has escrito se perderá.",
      },
      requestStatus: {
        open: "Recibida",
        in_progress: "En curso",
        waiting_customer: "Esperando tu respuesta",
        resolved: "Resuelta",
        closed: "Cerrada",
        cancelled: "Anulada",
      },
      request: {
        kind: "Tipo",
        filed: "Enviada el",
        about: "Sobre",
        cancel: "Anular la solicitud",
        cancelTitle: "¿Anular esta solicitud?",
        cancelBody: "Solo se puede anular mientras nadie la haya empezado.",
        cancelled: "Anulada",
        waitingOnYou: "Esperan tu respuesta",
        waitingOnYouHint: "Han preguntado algo y necesitan que contestes para seguir.",
        closed: "Cerrada el",
      },
      profile: {
        company: "Tu empresa",
        name: "Nombre",
        code: "Código",
        sites: "Centros",
        me: "Tus datos",
        emailNotices: "Avisarme por correo",
        password: "Contraseña",
        changePassword: "Cambiar la contraseña",
        passwordChanged: "Hecho. Las demás sesiones se han cerrado.",
        colleagues: "Quién más entra",
        remove: "Quitar acceso",
        removeTitle: "¿Quitar el acceso a {who}?",
        removeBody: "Dejará de poder entrar ahora mismo. Lo que ya pidió o firmó se queda.",
        invite: "Invitar a alguien",
        invited: "Invitación enviada",
        subtitle: "Tus datos, tu contraseña y quién más de tu empresa entra aquí.",
        everySite: "Ves todas las de tu empresa.",
        emailNoticesHint: "Un correo cuando haya algo esperando tu respuesta.",
        passwordHint: "Al cambiarla se cierran las sesiones de tus otros dispositivos.",
        you: "Tú",
        inviteTitle: "Dar acceso a alguien más",
        removed: "Acceso retirado",
      },
      skipToContent: "Ir al contenido",
    },
    customerPortal: {
      title: "Portal de cliente",
      saved: "Guardado",
      subtitle: "Lo que tus clientes ven, piden y aprueban por su cuenta",
      nav: {
        inbox: "Solicitudes",
        publications: "Publicaciones",
        accounts: "Accesos",
        settings: "Configuración del portal",
      },
      actions: {
        configure: "Configurar",
        revoke: "Quitar el acceso",
      },
      confirm: {
        revoke: "¿Quitar el acceso al portal a esta persona?",
      },
      overview: {
        off: "El portal está apagado: nadie de fuera puede entrar todavía.",
        turnOn: "Encenderlo",
        openRequests: "Solicitudes abiertas",
        awaiting: "Esperando al cliente",
        customers: "Clientes con acceso",
        accounts: "Personas con acceso",
        published: "Qué hay publicado",
        nothingPublished: "Todavía no se ha publicado nada",
        kind: "Tipo",
        visible: "Publicado",
        draft: "Sin publicar",
        offBadge: "Apagado",
        seeAll: "Ver todo",
        nothingPublishedHint: "En cuanto publiques algo, aparecerá aquí contado por tipo.",
        noRequests: "Nadie ha pedido nada todavía",
        noRequestsHint: "Lo que pidan tus clientes desde su portal entra en la bandeja.",
      },
      kinds: {
        project: "Proyectos",
        order: "Pedidos",
        asset: "Equipos",
        document: "Documentos",
        quote: "Presupuestos",
        invoice: "Facturas",
        contract: "Contratos",
        ticket: "Incidencias",
        maintenance: "Mantenimientos",
        meeting: "Reuniones",
      },
      visibility: {
        internal: "Solo dentro",
        customer: "Lo ve el cliente",
      },
      accountStatus: {
        invited: "Invitado",
        active: "Activo",
        suspended: "Suspendido",
        revoked: "Retirado",
      },
      requestStatus: {
        open: "Recibida",
        in_progress: "En curso",
        waiting_customer: "Esperando al cliente",
        resolved: "Resuelta",
        closed: "Cerrada",
        cancelled: "Anulada",
      },
      accounts: {
        title: "Quién entra al portal",
        subtitle: "Cada acceso es una llave a los datos de una empresa cliente",
        invite: "Dar acceso",
        search: "Persona, correo o cliente",
        none: "Todavía no hay nadie con acceso",
        customer: "Cliente",
        role: "Rol en el portal",
        roleHint: "Decide qué secciones ve y qué puede hacer en ellas.",
        sites: "Limitar a estos centros",
        sitesHint: "Sin marcar ninguno ve todos los centros de su empresa.",
        send: "Enviar la invitación",
        invited: "Invitación enviada",
        status: "Estado",
        allStatuses: "Todos",
        lastSeen: "Última vez",
        never: "Nunca ha entrado",
        resend: "Reenviar la invitación",
        resent: "Invitación reenviada",
        suspend: "Suspender",
        revoke: "Retirar el acceso",
        revokeTitle: "¿Retirar el acceso a {who}?",
        revokeBody: "Sus sesiones se cierran ahora. Lo que pidió y firmó se conserva con su nombre.",
        searchPlaceholder: "Nombre, correo o empresa",
        noneFiltered: "Ninguna cuenta con ese filtro",
        noneFilteredHint: "Prueba con otro nombre, otro correo u otro estado.",
        noneHint: "Invita al primer contacto de un cliente: recibirá un correo con un enlace para poner su contraseña.",
        openDetail: "Ver ficha",
        reinstate: "Reactivar",
        reinstated: "Cuenta reactivada",
        suspended: "Cuenta suspendida",
        revoked: "Acceso retirado",
        everySite: "Todas las suyas",
        someSites: "Solo 1 sede | Solo {n} sedes",
      },
      publications: {
        title: "Qué ve cada cliente",
        subtitle: "Nada sale fuera hasta que alguien lo publica",
        search: "Referencia, título o cliente",
        none: "Todavía no hay nada que publicar",
        reference: "Referencia",
        what: "Qué es",
        customer: "Cliente",
        unattributed: "Sin asignar",
        visibility: "Visibilidad",
        allKinds: "Todos los tipos",
        allVisibility: "Publicado y sin publicar",
        draftOnly: "Solo sin publicar",
        publishedOnly: "Solo publicado",
        internalStatus: "Estado interno",
        externalStatus: "Lo que lee el cliente",
        version: "Versión",
        whose: "¿De qué cliente es?",
        decisions: "Respuestas del cliente",
        decisionLine: "Versión {version}: {state} {who}",
        attribute: "Asignar a este cliente",
        askDecision: "Pedirle que lo apruebe",
        decisionAsked: "El cliente ya lo tiene pendiente",
        withdraw: "Retirar del portal",
        withdrawTitle: "¿Retirarlo del portal?",
        withdrawBody: "El cliente deja de verlo. Si ya lo había visto, es mejor decírselo.",
        publish: "Publicar",
        publishTitle: "¿Publicarlo para {who}?",
        publishBody: "A partir de ahora lo verán en su portal.",
        published: "Publicado",
        searchPlaceholder: "Referencia, título o cliente",
        noneFiltered: "Nada con ese filtro",
        noneFilteredHint: "Prueba con otra referencia, otro tipo u otro estado de publicación.",
        noneHint: "Aquí aparece lo que anuncian los demás módulos. Si está vacío, o no hay nada que anunciar o el tipo está en «nunca» en Ajustes.",
        versionShort: "v{version}",
        awaitingDecision: "Espera respuesta",
        untranslated: "Sin traducir todavía",
        whoseHint: "Sin cliente no se puede publicar: se enseñaría a la primera cuenta que preguntara.",
        withdrawn: "Retirado del portal",
      },
      inbox: {
        title: "Lo que piden los clientes",
        subtitle: "Todo lo que entra por el portal, en un sitio",
        search: "Referencia, asunto o cliente",
        none: "No hay solicitudes",
        code: "Referencia",
        subject: "Asunto",
        kind: "Tipo",
        when: "Cuándo",
        openOnly: "Solo abiertas",
        all: "Todas",
        filedBy: "Quién la envió",
        moveItOn: "Moverla",
        assignee: "Responsable",
        nobody: "Sin asignar",
        conversation: "Conversación",
        internalNote: "Nota interna",
        replyPlaceholder: "Escribe la respuesta",
        sendToCustomer: "Enviar al cliente",
        noteInternally: "Guardar como nota interna",
        searchPlaceholder: "Código, asunto o cliente",
        noneFiltered: "Ninguna solicitud con ese filtro",
        noneFilteredHint: "Prueba con otras palabras o cambia el estado.",
        noneHint: "Lo que pidan tus clientes desde su portal aparecerá aquí.",
        nothingSaid: "Todavía no se ha escrito nada.",
        reply: "Respuesta",
        replySent: "Enviado al cliente",
        noteSaved: "Nota guardada, solo la veis vosotros",
      },
      settings: {
        title: "Configuración del portal",
        subtitle: "Qué secciones hay, cómo se llaman y qué sale fuera",
        enabled: "Portal encendido",
        enabledHelp: "Apagado, nadie de fuera puede entrar aunque tenga contraseña.",
        welcome: "Frase de bienvenida",
        welcomeHint: "Sale arriba del todo en su pantalla de inicio.",
        footer: "Pie de página",
        brandName: "Nombre que ve el cliente",
        brandNameHint: "Vacío usa el nombre de la empresa.",
        logo: "Dirección del logotipo",
        sectionsHelp: "Enciende, renombra y ordena. Una sección que alimenta un módulo que no está instalado no se dibuja aunque esté encendida.",
        rename: "Llamarlo de otra forma",
        up: "Subir",
        down: "Bajar",
        fedBy: "La alimenta {module}",
        ownSection: "Del portal, no depende de nadie",
        publicationHelp: "Qué sale al portal y cuándo. Todo empieza en «nunca»: instalar el módulo no publica nada.",
        statesHelp: "Lo que dice el ERP, y lo que lee el cliente. La columna de la izquierda se llena sola con los estados que traen los registros; las que ya has traducido se quedan aunque ahora mismo no las lleve ninguno.",
        statesEmpty: "Todavía no ha llegado ningún estado que traducir.",
        seenTimes: "1 vez | {n} veces",
        requestTypesHelp: "Los tipos de solicitud son vuestros: añadid, quitad y renombrad.",
        newType: "Nombre del tipo nuevo",
        addType: "Añadir",
        rolesHelp: "Los roles del portal son de fuera: no son roles del ERP y no abren ninguna pantalla de aquí.",
        readOf: "Ver {section}",
        roleSaved: "Guardado. Alcanza a {count} personas.",
        aiHelp: "Cada una gasta del presupuesto de IA de la instancia.",
        aiDraft: "Ordenar lo que el cliente escribe",
        aiSummary: "Resumir cómo va la relación",
        aiItem: "Explicar una ficha en dos líneas",
        tabs: {
          general: "General",
          sections: "Secciones",
          publication: "Qué se publica",
          states: "Cómo se dice",
          requests: "Solicitudes",
          roles: "Roles",
          ai: "IA",
        },
        footerHint: "El pie de todas las pantallas del portal. Suele llevar el teléfono o la dirección.",
        logoHint: "La dirección de una imagen. Sustituye al nombre en la barra de arriba.",
        rule: "Cuándo se ve",
        sayItAs: "Cómo se dice fuera",
        statesEmptyHint: "Esta lista se llena sola con los estados que traigan los eventos. Todavía no ha llegado ninguno.",
        noFields: "Sin campos propios",
        noTypes: "Ningún tipo de solicitud",
        noTypesHint: "Sin tipos, tus clientes no pueden pedir nada. Añade al menos uno.",
        newTypeHint: "El nombre que verá el cliente: «Avería», «Recambio», «Consulta técnica».",
        removeTypeTitle: "¿Quitar «{what}»?",
        removeTypeBody: "Deja de ofrecerse a los clientes. Las solicitudes ya presentadas con este tipo se quedan como están.",
        saveRole: "Guardar el rol",
        notSeenYet: "Todavía no ha llegado ninguno",
      },
      rules: {
        never: "Nunca",
        on_request: "Cuando alguien lo publique",
        automatic: "Automáticamente",
      },
      rulesHelp: {
        never: "No se guarda ni se enseña nada de este tipo.",
        on_request: "Se guarda sin publicar; alguien decide cuál sale.",
        automatic: "Sale solo en cuanto llega.",
      },
      capabilities: {
        approve: "Aprobar y rechazar",
        request: "Pedir cosas",
        upload: "Subir archivos",
        comment: "Escribir en la conversación",
        confirm: "Confirmar entregas y fechas",
        dispute: "Reclamar",
        download: "Descargar documentos",
        manage_users: "Dar acceso a sus compañeros",
      },
      related: {
        accounts: "Portal",
        requests: "Pide por el portal",
      },
      dashboard: {
        openRequests: "Solicitudes abiertas",
        openRequestsHelp: "Cuántas cosas han pedido los clientes por el portal y siguen sin cerrar.",
        awaitingCustomer: "Esperando al cliente",
        awaitingCustomerHelp: "Cuántas cosas publicadas siguen sin respuesta del cliente: planos, presupuestos, fechas.",
        latestRequests: "Lo último que han pedido",
        latestRequestsHelp: "Las solicitudes abiertas más recientes, con el cliente que las envió.",
      },
      notifications: {
        categories: {
          request: "Solicitudes del portal",
          decision: "Respuestas del cliente",
          message: "Mensajes del portal",
        },
        request: {
          title: "Nueva solicitud en el portal",
          body: "{customer}: {subject}",
        },
        decision: {
          approved: "Un cliente ha aprobado",
          rejected: "Un cliente ha rechazado",
          changes: "Un cliente pide cambios",
          body: "{customer}: {reference}",
        },
        message: {
          title: "Mensaje en el portal",
          body: "{author}: {subject}",
        },
      },
      priorities: {
        low: "Baja",
        normal: "Normal",
        high: "Alta",
        urgent: "Urgente",
      },
      guide: {
        overviewStats: "Cuatro números: lo que te han pedido, lo que esperan de ti, cuántos clientes tienen portal y cuántas personas entran.",
        overviewPublished: "Cada tipo de registro, con lo que el cliente ya ve y lo que sigue en borrador. Muchos borradores y nada publicado es un portal que desde fuera se ve vacío.",
        publicationsDraft: "Todo lo que llega de otros módulos entra aquí en borrador y no se ve desde fuera. Publicar es un acto aparte, con su permiso y su rastro en auditoría.",
        publicationsVersion: "Si el contenido cambia, la versión sube y la respuesta que hubiera dado el cliente deja de contar. Nadie se queda con un «aprobado» sobre un plano viejo.",
        settingsEnabled: "Con el portal apagado nadie entra: sus cuentas reciben la misma respuesta que con una contraseña equivocada.",
        settingsPublication: "Cada tipo empieza en «nunca». «A petición» lo deja en borrador para que alguien lo publique; «automático» lo enseña en cuanto llega.",
      },
    },
    modules: {
      "customer-portal": "Portal de cliente",
    },
    permissions: {
      portal: {
        read: "Ver el portal: quién entra, qué piden y qué está publicado",
        publish: "Decidir qué sale fuera: publicar y retirar",
        respond: "Responder a los clientes y mover sus solicitudes",
        accounts: "Dar y quitar acceso al portal",
        configure: "Configurar el portal: secciones, tipos, estados y roles",
      },
    },
    settings: {
      mail: {
        "customer-portal__portal_invitation": {
          label: "Buzon desde el que se invita al portal",
          help: "Vacio usa el primer buzon configurado. Es la carta con la que un contacto elige su contrasena.",
        },
        "customer-portal__portal_notice": {
          label: "Buzon de los avisos del portal",
          help: "Vacio usa el primer buzon configurado. Avisa de que hay algo esperando en el portal, nunca lleva el documento.",
        },
      },
      ns: {
        "customer-portal": {
          title: "Portal de cliente",
          subtitle: "Cómo se llega al portal y cuánto dura una invitación",
          groups: {
            access: "Acceso",
          },
          portalUrl: {
            label: "Dirección pública del portal",
            help: "Es la que va en el enlace de las invitaciones. Vacía, el enlace no lleva a ninguna parte.",
          },
          invitationDays: {
            label: "Días que vale una invitación",
            help: "Una invitación que sigue funcionando en tres meses es una llave que alguien olvidó que cortó.",
          },
          supportEmail: {
            label: "Correo de ayuda del portal",
            help: "Sale en la pantalla de entrada. Vacío, no sale nada.",
          },
        },
      },
    },
    audit: {
      actions: {
        portal_configuration: {
          updated: "Cambió la configuración del portal",
        },
        portal_role: {
          created: "Creó un rol de portal",
          updated: "Cambió un rol de portal",
          deleted: "Borró un rol de portal",
        },
        portal_account: {
          granted: "Dio acceso al portal",
          invited: "Reenvió la invitación",
          updated: "Cambió un acceso al portal",
          revoked: "Retiró el acceso al portal",
          activated: "Estrenó su acceso al portal",
          password_changed: "Cambió su contraseña del portal",
        },
        portal_session: {
          started: "Entró en el portal",
          rejected: "Intento fallido de entrar en el portal",
        },
        portal_publication: {
          published: "Publicó algo en el portal",
          withdrawn: "Retiró algo del portal",
          attributed: "Asignó una publicación a un cliente",
          reassigned: "Una publicación cambió de cliente en su origen y se retiró",
        },
        portal_approval: {
          decided: "Un cliente respondió",
          requested: "Pidió una aprobación al cliente",
        },
        portal_request: {
          created: "Un cliente pidió algo",
          cancelled: "Un cliente anuló su solicitud",
          moved: "Movió una solicitud del portal",
        },
        portal_message: {
          sent: "Escribió al cliente",
          noted: "Dejó una nota interna",
        },
        portal_document: {
          uploaded: "Subió un archivo al portal",
          downloaded: "Un cliente descargó un documento",
          removed: "Quitó un archivo del portal",
        },
      },
    },
    assistant: {
      openers: {
        "customer-portal": {
          "whats-pending": "¿Qué no nos contestan los clientes?",
          "how-is-the-portal": "¿Cómo va el portal este mes?",
          "answer-a-request": "Responde a la última solicitud de…",
          "publish-a-record": "Publica este presupuesto al cliente",
        },
      },
    },
  },
  ca: {
    errors: {
      PORTAL_ROLE_TOO_WIDE: 'No pots donar ni treure més permisos dels que tens.',
    },
    portal: {
      title: "Portal de client",
      menu: "Menú",
      signOut: "Sortir",
      saved: "Desat",
      search: {
        open: "Cercar",
        title: "Cercar al teu portal",
        placeholder: "Referència, màquina, obra, factura…",
        none: "No hi ha res amb això",
        go: "Cercar",
        hint: "Escriu dues lletres o més i cerca a tot el que pots veure.",
        keepTyping: "Continua escrivint…",
      },
      sections: {
        home: "Inici",
        requests: "Sol·licituds",
        approvals: "Aprovacions",
        projects: "Projectes",
        orders: "Comandes",
        assets: "Equips",
        support: "Suport",
        maintenance: "Manteniment",
        documents: "Documents",
        quotes: "Pressupostos",
        invoices: "Factures",
        contracts: "Contractes",
        meetings: "Reunions",
        profile: "La meva empresa",
      },
      requestTypes: {
        support: "Avaria o incidència",
        quote: "Demanar pressupost",
        maintenance: "Demanar manteniment",
        part: "Demanar un recanvi",
        document: "Demanar documentació",
        meeting: "Demanar una reunió",
      },
      roles: {
        manager: "Responsable",
        operations: "Operacions",
        accounts: "Administració",
      },
      fields: {
        email: "Correu",
        password: "Contrasenya",
        newPassword: "Contrasenya nova",
        repeatPassword: "Repeteix la contrasenya",
        currentPassword: "Contrasenya actual",
        name: "Nom",
        jobTitle: "Càrrec",
        subject: "Assumpte",
        description: "Explica què passa",
        site: "Centre",
        anySite: "Qualsevol",
        about: "Sobre què",
        nothingInParticular: "Res en concret",
        symptom: "Què fa la màquina",
        since: "Des de quan",
        stopped: "La línia està aturada",
        need: "Què necessites",
        deadline: "Per a quan",
        preferred: "Data que us va bé",
        part: "Referència o descripció",
        quantity: "Quantitat",
        attendees: "Qui hi assistirà",
        aboutHint: "Així sabem de què parles sense haver de preguntar-t’ho.",
      },
      signIn: {
        title: "Entra al teu portal",
        subtitle: "Aquí tens el teu: el que està en marxa, el que espera la teva resposta i el que pots demanar.",
        emailPlaceholder: "el teu correu",
        go: "Entrar",
        forgot: "No recordo la contrasenya",
        forgotTitle: "Recuperar l’accés",
        forgotHelp: "Escriu el teu correu i t’enviem un enllaç per posar una contrasenya nova.",
        forgotGo: "Enviar enllaç",
        forgotSent: "Si aquesta adreça té accés, l’enllaç ja és en camí.",
        forgotSentHint: "Si aquesta adreça hi té accés, l’enllaç arriba en uns minuts i caduca.",
      },
      welcome: {
        title: "Tria la teva contrasenya",
        subtitle: "És l’últim pas: la tries tu i ningú més la veu.",
        go: "Entrar",
        mismatch: "Les dues contrasenyes no coincideixen",
        noToken: "Aquest enllaç no porta invitació. Demana’n un altre al teu proveïdor.",
        passwordHint: "Vuit caràcters o més.",
        noTokenHint: "Obre l’enllaç del correu tal com és: és d’un sol ús i caduca.",
        goToSignIn: "Vés a l’entrada",
      },
      home: {
        pending: "Pendent de tu",
        nothingPending: "No hi ha res esperant la teva resposta",
        inProgress: "En curs",
        upcoming: "Pròxim",
        activity: "El més recent",
        ask: "Què necessites?",
        summarise: "Resumeix-me com va tot",
        goHome: "Tornar a l’inici",
        greeting: "Hola, {name}",
        greetingAnon: "Hola",
        nothingPendingHint: "No hi ha res esperant la teva resposta.",
        summaryTitle: "Com va",
        brandNew: "Aquí encara no hi ha res",
        brandNewHint: "Quan hi hagi alguna cosa per ensenyar-te, apareixerà en aquesta pantalla. Mentrestant, pots escriure’ns des d’aquí.",
      },
      activity: {
        published: "Nou: {what}",
        replied: "{what} ha respost",
        wrote: "{what} va escriure",
      },
      card: {
        waitingOnYou: "Espera la teva resposta",
        on: "{date}",
        due: "Per al {date}",
      },
      decisionState: {
        pending: "Pendent",
        approved: "Aprovat",
        rejected: "Rebutjat",
        changes: "Canvis demanats",
        superseded: "Ja no compta: hi ha una versió nova",
        cancelled: "Anul·lat",
      },
      section: {
        ask: "Demanar alguna cosa",
        filter: "Cercar aquí",
        none: "Encara no hi ha res a {what}",
        noneFiltered: "No hi ha res que coincideixi",
        clear: "Treure el filtre",
        count: "1 element | {n} elements",
        noneHint: "Quan hi hagi alguna cosa per ensenyar-te en aquesta secció, apareixerà aquí.",
        noneFilteredHint: "Prova amb altres paraules.",
      },
      pager: {
        previous: "Anterior",
        next: "Següent",
        of: "Pàgina {page} de {pages}",
      },
      item: {
        back: "Tornar",
        status: "Estat",
        amount: "Import",
        date: "Data",
        dueDate: "Data prevista",
        decisionAsked: "Això espera la teva resposta (versió {version})",
        commentPlaceholder: "Si vols, explica’ns per què",
        approve: "Aprovar",
        changes: "Demanar canvis",
        reject: "Rebutjar",
        decisionSent: "Enviat. Ja ho tenen.",
        explain: "Explica-m’ho en dues línies",
        description: "Concepte",
        quantity: "Quantitat",
        unitPrice: "Preu",
        total: "Total",
        files: "Documents",
        decisions: "Respostes donades",
        versionIs: "· versió {version}",
        byWho: "· {who}",
        version: "Versió",
        lines: "Detall",
        progress: "Avanç",
        progressIs: "{percent}% fet",
        comment: "Comentari",
        commentHint: "Opcional, i ho llegeix qui t’ho ha enviat.",
        decidedOn: "Respost el {date}",
      },
      thread: {
        title: "Conversa",
        empty: "Encara no hi ha escrit ningú",
        placeholder: "Escriu aquí",
        send: "Enviar",
        attach: "Adjuntar",
        chosen: "{count} fitxer(s) a punt per enviar",
        removeFile: "Treure el fitxer",
      },
      requests: {
        new: "Nova sol·licitud",
        newTitle: "Què necessites?",
        none: "Encara no has demanat res",
        whatKind: "De què es tracta?",
        sayIt: "Digues-ho com vulguis",
        sayItPlaceholder: "La màquina fa un soroll estrany quan puja l’eix…",
        tidyUp: "Ordena-m’ho",
        stillMissing: "Amb això en sabrien més:",
        send: "Enviar la sol·licitud",
        sent: "Enviada. La seva referència és {code}.",
        tabs: {
          open: "Obertes",
          all: "Totes",
        },
        newSubtitle: "Explica’ns què necessites i qui ho porta et contestarà per aquí.",
        details: "Els detalls",
        noneOpen: "No tens res obert",
        noneHint: "El que ens demanis des d’aquí queda registrat, amb el seu número i la seva resposta.",
        noTypes: "De moment no es pot demanar res",
        noTypesHint: "Parla amb el teu contacte habitual.",
        leaveTitle: "Voleu deixar-ho a mitges?",
        leaveBody: "El que has escrit es perdrà.",
      },
      requestStatus: {
        open: "Rebuda",
        in_progress: "En curs",
        waiting_customer: "Esperant la teva resposta",
        resolved: "Resolta",
        closed: "Tancada",
        cancelled: "Anul·lada",
      },
      request: {
        kind: "Tipus",
        filed: "Enviada el",
        about: "Sobre",
        cancel: "Anul·lar la sol·licitud",
        cancelTitle: "Anul·lar aquesta sol·licitud?",
        cancelBody: "Només es pot anul·lar mentre ningú no l’hagi començat.",
        cancelled: "Anul·lada",
        waitingOnYou: "Esperen la teva resposta",
        waitingOnYouHint: "Han preguntat alguna cosa i necessiten que contestis per continuar.",
        closed: "Tancada el",
      },
      profile: {
        company: "La teva empresa",
        name: "Nom",
        code: "Codi",
        sites: "Centres",
        me: "Les teves dades",
        emailNotices: "Avisar-me per correu",
        password: "Contrasenya",
        changePassword: "Canviar la contrasenya",
        passwordChanged: "Fet. Les altres sessions s’han tancat.",
        colleagues: "Qui més hi entra",
        remove: "Treure accés",
        removeTitle: "Treure l’accés a {who}?",
        removeBody: "Deixarà de poder entrar ara mateix. El que ja va demanar o signar es queda.",
        invite: "Convidar algú",
        invited: "Invitació enviada",
        subtitle: "Les teves dades, la teva contrasenya i qui més de la teva empresa hi entra.",
        everySite: "Veus totes les de la teva empresa.",
        emailNoticesHint: "Un correu quan hi hagi alguna cosa esperant la teva resposta.",
        passwordHint: "En canviar-la es tanquen les sessions dels teus altres dispositius.",
        you: "Tu",
        inviteTitle: "Donar accés a algú més",
        removed: "Accés retirat",
      },
      skipToContent: "Vés al contingut",
    },
    customerPortal: {
      title: "Portal de client",
      saved: "Desat",
      subtitle: "El que els teus clients veuen, demanen i aproven pel seu compte",
      nav: {
        inbox: "Sol·licituds",
        publications: "Publicacions",
        accounts: "Accessos",
        settings: "Configuració del portal",
      },
      actions: {
        configure: "Configurar",
        revoke: "Treure l’accés",
      },
      confirm: {
        revoke: "Treure l’accés al portal a aquesta persona?",
      },
      overview: {
        off: "El portal està apagat: ningú de fora no hi pot entrar encara.",
        turnOn: "Encendre’l",
        openRequests: "Sol·licituds obertes",
        awaiting: "Esperant el client",
        customers: "Clients amb accés",
        accounts: "Persones amb accés",
        published: "Què hi ha publicat",
        nothingPublished: "Encara no s’ha publicat res",
        kind: "Tipus",
        visible: "Publicat",
        draft: "Sense publicar",
        offBadge: "Apagat",
        seeAll: "Veure-ho tot",
        nothingPublishedHint: "Quan publiquis alguna cosa, apareixerà aquí comptada per tipus.",
        noRequests: "Encara no ha demanat res ningú",
        noRequestsHint: "El que demanin els teus clients des del seu portal entra a la safata.",
      },
      kinds: {
        project: "Projectes",
        order: "Comandes",
        asset: "Equips",
        document: "Documents",
        quote: "Pressupostos",
        invoice: "Factures",
        contract: "Contractes",
        ticket: "Incidències",
        maintenance: "Manteniments",
        meeting: "Reunions",
      },
      visibility: {
        internal: "Només dins",
        customer: "Ho veu el client",
      },
      accountStatus: {
        invited: "Convidat",
        active: "Actiu",
        suspended: "Suspès",
        revoked: "Retirat",
      },
      requestStatus: {
        open: "Rebuda",
        in_progress: "En curs",
        waiting_customer: "Esperant el client",
        resolved: "Resolta",
        closed: "Tancada",
        cancelled: "Anul·lada",
      },
      accounts: {
        title: "Qui entra al portal",
        subtitle: "Cada accés és una clau a les dades d’una empresa clienta",
        invite: "Donar accés",
        search: "Persona, correu o client",
        none: "Encara no hi ha ningú amb accés",
        customer: "Client",
        role: "Rol al portal",
        roleHint: "Decideix quines seccions veu i què hi pot fer.",
        sites: "Limitar a aquests centres",
        sitesHint: "Sense marcar-ne cap veu tots els centres de la seva empresa.",
        send: "Enviar la invitació",
        invited: "Invitació enviada",
        status: "Estat",
        allStatuses: "Tots",
        lastSeen: "Darrera vegada",
        never: "No hi ha entrat mai",
        resend: "Reenviar la invitació",
        resent: "Invitació reenviada",
        suspend: "Suspendre",
        revoke: "Retirar l’accés",
        revokeTitle: "Retirar l’accés a {who}?",
        revokeBody: "Les seves sessions es tanquen ara. El que va demanar i signar es conserva amb el seu nom.",
        searchPlaceholder: "Nom, correu o empresa",
        noneFiltered: "Cap compte amb aquest filtre",
        noneFilteredHint: "Prova amb un altre nom, un altre correu o un altre estat.",
        noneHint: "Convida el primer contacte d’un client: rebrà un correu amb un enllaç per posar la seva contrasenya.",
        openDetail: "Veure fitxa",
        reinstate: "Reactivar",
        reinstated: "Compte reactivat",
        suspended: "Compte suspès",
        revoked: "Accés retirat",
        everySite: "Totes les seves",
        someSites: "Només 1 seu | Només {n} seus",
      },
      publications: {
        title: "Què veu cada client",
        subtitle: "Res no surt a fora fins que algú ho publica",
        search: "Referència, títol o client",
        none: "Encara no hi ha res per publicar",
        reference: "Referència",
        what: "Què és",
        customer: "Client",
        unattributed: "Sense assignar",
        visibility: "Visibilitat",
        allKinds: "Tots els tipus",
        allVisibility: "Publicat i sense publicar",
        draftOnly: "Només sense publicar",
        publishedOnly: "Només publicat",
        internalStatus: "Estat intern",
        externalStatus: "El que llegeix el client",
        version: "Versió",
        whose: "De quin client és?",
        decisions: "Respostes del client",
        decisionLine: "Versió {version}: {state} {who}",
        attribute: "Assignar a aquest client",
        askDecision: "Demanar-li que ho aprovi",
        decisionAsked: "El client ja ho té pendent",
        withdraw: "Retirar del portal",
        withdrawTitle: "Retirar-ho del portal?",
        withdrawBody: "El client deixa de veure-ho. Si ja ho havia vist, val més dir-l’hi.",
        publish: "Publicar",
        publishTitle: "Publicar-ho per a {who}?",
        publishBody: "A partir d’ara ho veuran al seu portal.",
        published: "Publicat",
        searchPlaceholder: "Referència, títol o client",
        noneFiltered: "Res amb aquest filtre",
        noneFilteredHint: "Prova amb una altra referència, un altre tipus o un altre estat de publicació.",
        noneHint: "Aquí apareix el que anuncien els altres mòduls. Si és buit, o no hi ha res a anunciar o el tipus és a «mai» a Configuració.",
        versionShort: "v{version}",
        awaitingDecision: "Espera resposta",
        untranslated: "Encara sense traduir",
        whoseHint: "Sense client no es pot publicar: s’ensenyaria al primer compte que ho demanés.",
        withdrawn: "Retirat del portal",
      },
      inbox: {
        title: "El que demanen els clients",
        subtitle: "Tot el que entra pel portal, en un lloc",
        search: "Referència, assumpte o client",
        none: "No hi ha sol·licituds",
        code: "Referència",
        subject: "Assumpte",
        kind: "Tipus",
        when: "Quan",
        openOnly: "Només obertes",
        all: "Totes",
        filedBy: "Qui l’ha enviada",
        moveItOn: "Moure-la",
        assignee: "Responsable",
        nobody: "Sense assignar",
        conversation: "Conversa",
        internalNote: "Nota interna",
        replyPlaceholder: "Escriu la resposta",
        sendToCustomer: "Enviar al client",
        noteInternally: "Desar com a nota interna",
        searchPlaceholder: "Codi, assumpte o client",
        noneFiltered: "Cap sol·licitud amb aquest filtre",
        noneFilteredHint: "Prova amb altres paraules o canvia l’estat.",
        noneHint: "El que demanin els teus clients des del seu portal apareixerà aquí.",
        nothingSaid: "Encara no s’hi ha escrit res.",
        reply: "Resposta",
        replySent: "Enviat al client",
        noteSaved: "Nota desada, només la veieu vosaltres",
      },
      settings: {
        title: "Configuració del portal",
        subtitle: "Quines seccions hi ha, com es diuen i què surt a fora",
        enabled: "Portal encès",
        enabledHelp: "Apagat, ningú de fora no hi pot entrar encara que tingui contrasenya.",
        welcome: "Frase de benvinguda",
        welcomeHint: "Surt a dalt de tot a la seva pantalla d’inici.",
        footer: "Peu de pàgina",
        brandName: "Nom que veu el client",
        brandNameHint: "Buit fa servir el nom de l’empresa.",
        logo: "Adreça del logotip",
        sectionsHelp: "Encén, reanomena i ordena. Una secció que alimenta un mòdul que no està instal·lat no es dibuixa encara que estigui encesa.",
        rename: "Anomenar-ho d’una altra manera",
        up: "Pujar",
        down: "Baixar",
        fedBy: "L’alimenta {module}",
        ownSection: "Del portal, no depèn de ningú",
        publicationHelp: "Què surt al portal i quan. Tot comença a «mai»: instal·lar el mòdul no publica res.",
        statesHelp: "El que diu l’ERP, i el que llegeix el client. La columna de l’esquerra s’omple sola amb els estats que porten els registres; les que ja has traduït es queden encara que ara mateix no en porti cap.",
        statesEmpty: "Encara no ha arribat cap estat per traduir.",
        seenTimes: "1 vegada | {n} vegades",
        requestTypesHelp: "Els tipus de sol·licitud són vostres: afegiu, traieu i reanomeneu.",
        newType: "Nom del tipus nou",
        addType: "Afegir",
        rolesHelp: "Els rols del portal són de fora: no són rols de l’ERP i no obren cap pantalla d’aquí.",
        readOf: "Veure {section}",
        roleSaved: "Desat. Arriba a {count} persones.",
        aiHelp: "Cadascuna gasta del pressupost d’IA de la instància.",
        aiDraft: "Ordenar el que el client escriu",
        aiSummary: "Resumir com va la relació",
        aiItem: "Explicar una fitxa en dues línies",
        tabs: {
          general: "General",
          sections: "Seccions",
          publication: "Què es publica",
          states: "Com es diu",
          requests: "Sol·licituds",
          roles: "Rols",
          ai: "IA",
        },
        footerHint: "El peu de totes les pantalles del portal. Sol portar el telèfon o l’adreça.",
        logoHint: "L’adreça d’una imatge. Substitueix el nom a la barra de dalt.",
        rule: "Quan es veu",
        sayItAs: "Com es diu a fora",
        statesEmptyHint: "Aquesta llista s’omple sola amb els estats que portin els esdeveniments. Encara no n’ha arribat cap.",
        noFields: "Sense camps propis",
        noTypes: "Cap tipus de sol·licitud",
        noTypesHint: "Sense tipus, els teus clients no poden demanar res. Afegeix-ne almenys un.",
        newTypeHint: "El nom que veurà el client: «Avaria», «Recanvi», «Consulta tècnica».",
        removeTypeTitle: "Voleu treure «{what}»?",
        removeTypeBody: "Deixa d’oferir-se als clients. Les sol·licituds ja presentades amb aquest tipus es queden com estan.",
        saveRole: "Desar el rol",
        notSeenYet: "Encara no n’ha arribat cap",
      },
      rules: {
        never: "Mai",
        on_request: "Quan algú ho publiqui",
        automatic: "Automàticament",
      },
      rulesHelp: {
        never: "No es desa ni s’ensenya res d’aquest tipus.",
        on_request: "Es desa sense publicar; algú decideix quin surt.",
        automatic: "Surt sol tan bon punt arriba.",
      },
      capabilities: {
        approve: "Aprovar i rebutjar",
        request: "Demanar coses",
        upload: "Pujar fitxers",
        comment: "Escriure a la conversa",
        confirm: "Confirmar lliuraments i dates",
        dispute: "Reclamar",
        download: "Descarregar documents",
        manage_users: "Donar accés als seus companys",
      },
      related: {
        accounts: "Portal",
        requests: "Demana pel portal",
      },
      dashboard: {
        openRequests: "Sol·licituds obertes",
        openRequestsHelp: "Quantes coses han demanat els clients pel portal i encara no s’han tancat.",
        awaitingCustomer: "Esperant el client",
        awaitingCustomerHelp: "Quantes coses publicades segueixen sense resposta del client: plànols, pressupostos, dates.",
        latestRequests: "El darrer que han demanat",
        latestRequestsHelp: "Les sol·licituds obertes més recents, amb el client que les va enviar.",
      },
      notifications: {
        categories: {
          request: "Sol·licituds del portal",
          decision: "Respostes del client",
          message: "Missatges del portal",
        },
        request: {
          title: "Nova sol·licitud al portal",
          body: "{customer}: {subject}",
        },
        decision: {
          approved: "Un client ho ha aprovat",
          rejected: "Un client ho ha rebutjat",
          changes: "Un client demana canvis",
          body: "{customer}: {reference}",
        },
        message: {
          title: "Missatge al portal",
          body: "{author}: {subject}",
        },
      },
      priorities: {
        low: "Baixa",
        normal: "Normal",
        high: "Alta",
        urgent: "Urgent",
      },
      guide: {
        overviewStats: "Quatre números: el que t’han demanat, el que esperen de tu, quants clients tenen portal i quantes persones hi entren.",
        overviewPublished: "Cada tipus de registre, amb el que el client ja veu i el que continua en esborrany. Molts esborranys i res publicat és un portal que des de fora es veu buit.",
        publicationsDraft: "Tot el que arriba d’altres mòduls entra aquí en esborrany i no es veu des de fora. Publicar és un acte a part, amb el seu permís i el seu rastre a auditoria.",
        publicationsVersion: "Si el contingut canvia, la versió puja i la resposta que hagués donat el client deixa de comptar. Ningú no es queda amb un «aprovat» sobre un plànol vell.",
        settingsEnabled: "Amb el portal apagat no hi entra ningú: els seus comptes reben la mateixa resposta que amb una contrasenya equivocada.",
        settingsPublication: "Cada tipus comença a «mai». «A petició» el deixa en esborrany perquè algú el publiqui; «automàtic» l’ensenya així que arriba.",
      },
    },
    modules: {
      "customer-portal": "Portal de client",
    },
    permissions: {
      portal: {
        read: "Veure el portal: qui hi entra, què demanen i què està publicat",
        publish: "Decidir què surt a fora: publicar i retirar",
        respond: "Respondre als clients i moure les seves sol·licituds",
        accounts: "Donar i treure accés al portal",
        configure: "Configurar el portal: seccions, tipus, estats i rols",
      },
    },
    settings: {
      mail: {
        "customer-portal__portal_invitation": {
          label: "Bustia des de la qual es convida al portal",
          help: "Buida fa servir la primera bustia configurada. Es la carta amb que un contacte tria la seva contrasenya.",
        },
        "customer-portal__portal_notice": {
          label: "Bustia dels avisos del portal",
          help: "Buida fa servir la primera bustia configurada. Avisa que hi ha alguna cosa esperant al portal, mai no porta el document.",
        },
      },
      ns: {
        "customer-portal": {
          title: "Portal de client",
          subtitle: "Com s’arriba al portal i quant dura una invitació",
          groups: {
            access: "Accés",
          },
          portalUrl: {
            label: "Adreça pública del portal",
            help: "És la que va a l’enllaç de les invitacions. Buida, l’enllaç no porta enlloc.",
          },
          invitationDays: {
            label: "Dies que val una invitació",
            help: "Una invitació que segueix funcionant d’aquí a tres mesos és una clau que algú va oblidar que va tallar.",
          },
          supportEmail: {
            label: "Correu d’ajuda del portal",
            help: "Surt a la pantalla d’entrada. Buit, no surt res.",
          },
        },
      },
    },
    audit: {
      actions: {
        portal_configuration: {
          updated: "Va canviar la configuració del portal",
        },
        portal_role: {
          created: "Va crear un rol de portal",
          updated: "Va canviar un rol de portal",
          deleted: "Va esborrar un rol de portal",
        },
        portal_account: {
          granted: "Va donar accés al portal",
          invited: "Va reenviar la invitació",
          updated: "Va canviar un accés al portal",
          revoked: "Va retirar l’accés al portal",
          activated: "Va estrenar el seu accés al portal",
          password_changed: "Va canviar la seva contrasenya del portal",
        },
        portal_session: {
          started: "Va entrar al portal",
          rejected: "Intent fallit d’entrar al portal",
        },
        portal_publication: {
          published: "Va publicar alguna cosa al portal",
          withdrawn: "Va retirar alguna cosa del portal",
          attributed: "Va assignar una publicació a un client",
          reassigned: "Una publicació va canviar de client a l'origen i es va retirar",
        },
        portal_approval: {
          decided: "Un client va respondre",
          requested: "Va demanar una aprovació al client",
        },
        portal_request: {
          created: "Un client va demanar alguna cosa",
          cancelled: "Un client va anul·lar la seva sol·licitud",
          moved: "Va moure una sol·licitud del portal",
        },
        portal_message: {
          sent: "Va escriure al client",
          noted: "Va deixar una nota interna",
        },
        portal_document: {
          uploaded: "Va pujar un fitxer al portal",
          downloaded: "Un client va descarregar un document",
          removed: "Va treure un fitxer del portal",
        },
      },
    },
    assistant: {
      openers: {
        "customer-portal": {
          "whats-pending": "Què no ens contesten els clients?",
          "how-is-the-portal": "Com va el portal aquest mes?",
          "answer-a-request": "Respon a la darrera sol·licitud de…",
          "publish-a-record": "Publica aquest pressupost al client",
        },
      },
    },
  },
  en: {
    errors: {
      PORTAL_ROLE_TOO_WIDE: 'You cannot grant or take away more than you hold.',
    },
    portal: {
      title: "Customer portal",
      menu: "Menu",
      signOut: "Sign out",
      saved: "Saved",
      search: {
        open: "Search",
        title: "Search your portal",
        placeholder: "Reference, machine, job, invoice…",
        none: "Nothing matches that",
        go: "Search",
        hint: "Type two letters or more to search everything you can see.",
        keepTyping: "Keep typing…",
      },
      sections: {
        home: "Home",
        requests: "Requests",
        approvals: "Approvals",
        projects: "Projects",
        orders: "Orders",
        assets: "Equipment",
        support: "Support",
        maintenance: "Maintenance",
        documents: "Documents",
        quotes: "Quotes",
        invoices: "Invoices",
        contracts: "Contracts",
        meetings: "Meetings",
        profile: "My company",
      },
      requestTypes: {
        support: "Breakdown or issue",
        quote: "Ask for a quote",
        maintenance: "Ask for maintenance",
        part: "Ask for a spare part",
        document: "Ask for a document",
        meeting: "Ask for a meeting",
      },
      roles: {
        manager: "Manager",
        operations: "Operations",
        accounts: "Accounts",
      },
      fields: {
        email: "Email",
        password: "Password",
        newPassword: "New password",
        repeatPassword: "Repeat the password",
        currentPassword: "Current password",
        name: "Name",
        jobTitle: "Job title",
        subject: "Subject",
        description: "Tell us what is happening",
        site: "Site",
        anySite: "Any",
        about: "What it is about",
        nothingInParticular: "Nothing in particular",
        symptom: "What the machine does",
        since: "Since when",
        stopped: "The line is stopped",
        need: "What you need",
        deadline: "By when",
        preferred: "A date that suits you",
        part: "Reference or description",
        quantity: "Quantity",
        attendees: "Who will attend",
        aboutHint: "So we know what you are talking about without having to ask.",
      },
      signIn: {
        title: "Sign in to your portal",
        subtitle: "Everything of yours: what is under way, what is waiting for your answer, and what you can ask for.",
        emailPlaceholder: "your email",
        go: "Sign in",
        forgot: "I forgot my password",
        forgotTitle: "Recover your access",
        forgotHelp: "Type your email and we will send you a link to set a new password.",
        forgotGo: "Send the link",
        forgotSent: "If that address has access, the link is on its way.",
        forgotSentHint: "If that address has access, the link arrives within minutes and expires.",
      },
      welcome: {
        title: "Choose your password",
        subtitle: "Last step: you choose it and nobody else ever sees it.",
        go: "Sign in",
        mismatch: "The two passwords do not match",
        noToken: "This link carries no invitation. Ask your supplier for another one.",
        passwordHint: "Eight characters or more.",
        noTokenHint: "Open the link from the letter as it is: it is single-use and it expires.",
        goToSignIn: "Go to the sign-in screen",
      },
      home: {
        pending: "Waiting on you",
        nothingPending: "Nothing is waiting for your answer",
        inProgress: "Under way",
        upcoming: "Coming up",
        activity: "Latest",
        ask: "What do you need?",
        summarise: "Sum up how things stand",
        goHome: "Back to the start",
        greeting: "Hello, {name}",
        greetingAnon: "Hello",
        nothingPendingHint: "Nothing is waiting on you.",
        summaryTitle: "Where things stand",
        brandNew: "There is nothing here yet",
        brandNewHint: "As soon as there is something to show you it will appear on this screen. In the meantime you can write to us from here.",
      },
      activity: {
        published: "New: {what}",
        replied: "{what} replied",
        wrote: "{what} wrote",
      },
      card: {
        waitingOnYou: "Waiting on you",
        on: "{date}",
        due: "Due {date}",
      },
      decisionState: {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        changes: "Changes requested",
        superseded: "No longer counts: there is a newer version",
        cancelled: "Cancelled",
      },
      section: {
        ask: "Ask for something",
        filter: "Search here",
        none: "Nothing in {what} yet",
        noneFiltered: "Nothing matches",
        clear: "Clear the filter",
        count: "1 item | {n} items",
        noneHint: "When there is something to show you in this section it will appear here.",
        noneFilteredHint: "Try other words.",
      },
      pager: {
        previous: "Previous",
        next: "Next",
        of: "Page {page} of {pages}",
      },
      item: {
        back: "Back",
        status: "Status",
        amount: "Amount",
        date: "Date",
        dueDate: "Expected date",
        decisionAsked: "This is waiting for your answer (version {version})",
        commentPlaceholder: "Tell us why, if you like",
        approve: "Approve",
        changes: "Ask for changes",
        reject: "Reject",
        decisionSent: "Sent. They have it.",
        explain: "Explain it in two lines",
        description: "Description",
        quantity: "Quantity",
        unitPrice: "Unit price",
        total: "Total",
        files: "Documents",
        decisions: "Answers given",
        versionIs: "· version {version}",
        byWho: "· {who}",
        version: "Version",
        lines: "Breakdown",
        progress: "Progress",
        progressIs: "{percent}% done",
        comment: "Comment",
        commentHint: "Optional, and read by whoever sent it to you.",
        decidedOn: "Answered on {date}",
      },
      thread: {
        title: "Conversation",
        empty: "Nobody has written yet",
        placeholder: "Write here",
        send: "Send",
        attach: "Attach",
        chosen: "{count} file(s) ready to send",
        removeFile: "Remove the file",
      },
      requests: {
        new: "New request",
        newTitle: "What do you need?",
        none: "You have not asked for anything yet",
        whatKind: "What is it about?",
        sayIt: "Say it however you like",
        sayItPlaceholder: "The machine makes an odd noise when the axis goes up…",
        tidyUp: "Tidy it up for me",
        stillMissing: "These would help them:",
        send: "Send the request",
        sent: "Sent. Its reference is {code}.",
        tabs: {
          open: "Open",
          all: "All",
        },
        newSubtitle: "Tell us what you need and whoever handles it will answer you here.",
        details: "The details",
        noneOpen: "You have nothing open",
        noneHint: "Anything you ask for here is filed, with its own number and its answer.",
        noTypes: "Nothing can be asked for right now",
        noTypesHint: "Talk to your usual contact.",
        leaveTitle: "Leave this half-written?",
        leaveBody: "What you have written will be lost.",
      },
      requestStatus: {
        open: "Received",
        in_progress: "Under way",
        waiting_customer: "Waiting on you",
        resolved: "Resolved",
        closed: "Closed",
        cancelled: "Cancelled",
      },
      request: {
        kind: "Kind",
        filed: "Sent on",
        about: "About",
        cancel: "Cancel the request",
        cancelTitle: "Cancel this request?",
        cancelBody: "It can only be cancelled while nobody has started it.",
        cancelled: "Cancelled",
        waitingOnYou: "They are waiting on you",
        waitingOnYouHint: "They have asked something and need your answer to carry on.",
        closed: "Closed on",
      },
      profile: {
        company: "Your company",
        name: "Name",
        code: "Code",
        sites: "Sites",
        me: "Your details",
        emailNotices: "Let me know by email",
        password: "Password",
        changePassword: "Change the password",
        passwordChanged: "Done. Your other sessions were closed.",
        colleagues: "Who else has access",
        remove: "Remove access",
        removeTitle: "Remove access for {who}?",
        removeBody: "They stop being able to sign in immediately. What they already asked for or signed stays.",
        invite: "Invite somebody",
        invited: "Invitation sent",
        subtitle: "Your details, your password and who else from your company signs in here.",
        everySite: "You can see all of your company’s.",
        emailNoticesHint: "A letter when something is waiting on you.",
        passwordHint: "Changing it signs your other devices out.",
        you: "You",
        inviteTitle: "Give somebody else access",
        removed: "Access removed",
      },
      skipToContent: "Skip to the content",
    },
    customerPortal: {
      title: "Customer portal",
      saved: "Saved",
      subtitle: "What your customers see, ask for and approve on their own",
      nav: {
        inbox: "Requests",
        publications: "Publications",
        accounts: "Access",
        settings: "Portal configuration",
      },
      actions: {
        configure: "Configure",
        revoke: "Revoke access",
      },
      confirm: {
        revoke: "Revoke this person’s portal access?",
      },
      overview: {
        off: "The portal is switched off: nobody outside can get in yet.",
        turnOn: "Switch it on",
        openRequests: "Open requests",
        awaiting: "Waiting on the customer",
        customers: "Customers with access",
        accounts: "People with access",
        published: "What is published",
        nothingPublished: "Nothing has been published yet",
        kind: "Kind",
        visible: "Published",
        draft: "Not published",
        offBadge: "Off",
        seeAll: "See all",
        nothingPublishedHint: "As soon as you publish something it appears here, counted by kind.",
        noRequests: "Nobody has asked for anything yet",
        noRequestsHint: "What your customers ask for in their portal lands in the inbox.",
      },
      kinds: {
        project: "Projects",
        order: "Orders",
        asset: "Equipment",
        document: "Documents",
        quote: "Quotes",
        invoice: "Invoices",
        contract: "Contracts",
        ticket: "Issues",
        maintenance: "Maintenance",
        meeting: "Meetings",
      },
      visibility: {
        internal: "Inside only",
        customer: "The customer sees it",
      },
      accountStatus: {
        invited: "Invited",
        active: "Active",
        suspended: "Suspended",
        revoked: "Revoked",
      },
      requestStatus: {
        open: "Received",
        in_progress: "Under way",
        waiting_customer: "Waiting on the customer",
        resolved: "Resolved",
        closed: "Closed",
        cancelled: "Cancelled",
      },
      accounts: {
        title: "Who gets into the portal",
        subtitle: "Every account is a key to one customer company’s data",
        invite: "Give access",
        search: "Person, email or customer",
        none: "Nobody has access yet",
        customer: "Customer",
        role: "Portal role",
        roleHint: "Decides which sections they see and what they may do there.",
        sites: "Limit to these sites",
        sitesHint: "With none ticked they see every site of their company.",
        send: "Send the invitation",
        invited: "Invitation sent",
        status: "Status",
        allStatuses: "All",
        lastSeen: "Last seen",
        never: "Never signed in",
        resend: "Send the invitation again",
        resent: "Invitation sent again",
        suspend: "Suspend",
        revoke: "Revoke access",
        revokeTitle: "Revoke access for {who}?",
        revokeBody: "Their sessions close now. What they asked for and signed is kept with their name on it.",
        searchPlaceholder: "Name, address or company",
        noneFiltered: "No account matches that filter",
        noneFilteredHint: "Try another name, another address or another status.",
        noneHint: "Invite a customer’s first contact: they get a letter with a link to set their own password.",
        openDetail: "Open",
        reinstate: "Reinstate",
        reinstated: "Account reinstated",
        suspended: "Account suspended",
        revoked: "Access revoked",
        everySite: "All of theirs",
        someSites: "Only 1 site | Only {n} sites",
      },
      publications: {
        title: "What each customer sees",
        subtitle: "Nothing leaves the building until somebody publishes it",
        search: "Reference, title or customer",
        none: "There is nothing to publish yet",
        reference: "Reference",
        what: "What it is",
        customer: "Customer",
        unattributed: "Unattributed",
        visibility: "Visibility",
        allKinds: "Every kind",
        allVisibility: "Published and not",
        draftOnly: "Not published only",
        publishedOnly: "Published only",
        internalStatus: "Internal status",
        externalStatus: "What the customer reads",
        version: "Version",
        whose: "Whose is this?",
        decisions: "The customer’s answers",
        decisionLine: "Version {version}: {state} {who}",
        attribute: "Attribute to this customer",
        askDecision: "Ask them to approve it",
        decisionAsked: "It is now waiting on the customer",
        withdraw: "Take it off the portal",
        withdrawTitle: "Take it off the portal?",
        withdrawBody: "The customer stops seeing it. If they already saw it, tell them.",
        publish: "Publish",
        publishTitle: "Publish it for {who}?",
        publishBody: "From now on they see it in their portal.",
        published: "Published",
        searchPlaceholder: "Reference, title or customer",
        noneFiltered: "Nothing matches that filter",
        noneFilteredHint: "Try another reference, another kind or another publication state.",
        noneHint: "What the other modules announce shows up here. Empty means either nothing to announce, or that kind is set to \"never\" in the settings.",
        versionShort: "v{version}",
        awaitingDecision: "Awaiting an answer",
        untranslated: "Not translated yet",
        whoseHint: "It cannot be published without one: it would be shown to the first account that asked.",
        withdrawn: "Withdrawn from the portal",
      },
      inbox: {
        title: "What the customers ask for",
        subtitle: "Everything that comes in through the portal, in one place",
        search: "Reference, subject or customer",
        none: "No requests",
        code: "Reference",
        subject: "Subject",
        kind: "Kind",
        when: "When",
        openOnly: "Open only",
        all: "All",
        filedBy: "Who sent it",
        moveItOn: "Move it on",
        assignee: "Assignee",
        nobody: "Nobody",
        conversation: "Conversation",
        internalNote: "Internal note",
        replyPlaceholder: "Write the reply",
        sendToCustomer: "Send to the customer",
        noteInternally: "Save as an internal note",
        searchPlaceholder: "Code, subject or customer",
        noneFiltered: "No request matches that filter",
        noneFilteredHint: "Try other words, or change the status.",
        noneHint: "What your customers ask for in their portal will appear here.",
        nothingSaid: "Nothing has been written yet.",
        reply: "Reply",
        replySent: "Sent to the customer",
        noteSaved: "Note saved, and only you can see it",
      },
      settings: {
        title: "Portal configuration",
        subtitle: "Which sections exist, what they are called, and what goes out",
        enabled: "Portal switched on",
        enabledHelp: "Switched off, nobody outside can get in even with a password.",
        welcome: "Welcome line",
        welcomeHint: "It sits at the top of their home screen.",
        footer: "Footer",
        brandName: "Name the customer sees",
        brandNameHint: "Empty uses the company name.",
        logo: "Logo address",
        sectionsHelp: "Switch on, rename and order. A section fed by a module that is not installed is not drawn even when it is on.",
        rename: "Call it something else",
        up: "Up",
        down: "Down",
        fedBy: "Fed by {module}",
        ownSection: "The portal’s own, depends on nothing",
        publicationHelp: "What goes out to the portal, and when. Everything starts at \"never\": installing the module publishes nothing.",
        statesHelp: "What the ERP says, and what the customer reads. The left-hand column fills itself with the statuses the records actually carry; the ones you have already translated stay, even where nothing carries them right now.",
        statesEmpty: "No status has arrived to translate yet.",
        seenTimes: "once | {n} times",
        requestTypesHelp: "The request types are yours: add, remove and rename them.",
        newType: "Name of the new kind",
        addType: "Add",
        rolesHelp: "Portal roles are for outside: they are not ERP roles and they open no screen in here.",
        readOf: "See {section}",
        roleSaved: "Saved. It reaches {count} people.",
        aiHelp: "Each one spends from the instance’s AI budget.",
        aiDraft: "Tidy up what the customer writes",
        aiSummary: "Sum up how the relationship stands",
        aiItem: "Explain one record in two lines",
        tabs: {
          general: "General",
          sections: "Sections",
          publication: "What is published",
          states: "How it is said",
          requests: "Requests",
          roles: "Roles",
          ai: "AI",
        },
        footerHint: "The footer of every portal screen. Usually a phone number or an address.",
        logoHint: "The address of an image. It replaces the name in the top bar.",
        rule: "When it is shown",
        sayItAs: "How it is said outside",
        statesEmptyHint: "This list fills itself with the statuses the events actually carry. None has arrived yet.",
        noFields: "No fields of its own",
        noTypes: "No request types",
        noTypesHint: "With no types your customers cannot ask for anything. Add at least one.",
        newTypeHint: "The name the customer will see: \"Breakdown\", \"Spare part\", \"Technical question\".",
        removeTypeTitle: "Remove \"{what}\"?",
        removeTypeBody: "It stops being offered to customers. Requests already filed under it stay exactly as they are.",
        saveRole: "Save the role",
        notSeenYet: "None has arrived yet",
      },
      rules: {
        never: "Never",
        on_request: "When somebody publishes it",
        automatic: "Automatically",
      },
      rulesHelp: {
        never: "Nothing of this kind is kept or shown.",
        on_request: "Kept unpublished; somebody decides which ones go out.",
        automatic: "Goes out on its own as soon as it arrives.",
      },
      capabilities: {
        approve: "Approve and reject",
        request: "Ask for things",
        upload: "Upload files",
        comment: "Write in the conversation",
        confirm: "Confirm deliveries and dates",
        dispute: "Dispute",
        download: "Download documents",
        manage_users: "Give access to their colleagues",
      },
      related: {
        accounts: "Portal",
        requests: "Portal requests",
      },
      dashboard: {
        openRequests: "Open portal requests",
        openRequestsHelp: "How many things customers have asked for through the portal and are still open.",
        awaitingCustomer: "Waiting on the customer",
        awaitingCustomerHelp: "How many published things are still unanswered by the customer: drawings, quotes, dates.",
        latestRequests: "The latest requests",
        latestRequestsHelp: "The most recent open requests, with the customer who sent them.",
      },
      notifications: {
        categories: {
          request: "Portal requests",
          decision: "Customer answers",
          message: "Portal messages",
        },
        request: {
          title: "New portal request",
          body: "{customer}: {subject}",
        },
        decision: {
          approved: "A customer approved",
          rejected: "A customer rejected",
          changes: "A customer asked for changes",
          body: "{customer}: {reference}",
        },
        message: {
          title: "A portal message",
          body: "{author}: {subject}",
        },
      },
      priorities: {
        low: "Low",
        normal: "Normal",
        high: "High",
        urgent: "Urgent",
      },
      guide: {
        overviewStats: "Four figures: what they have asked for, what they are waiting on you for, how many customers have a portal and how many people sign in.",
        overviewPublished: "Every kind of record, with what the customer can already see and what is still a draft. Many drafts and nothing published is a portal that looks empty from outside.",
        publicationsDraft: "Everything arriving from other modules lands here as a draft and is invisible from outside. Publishing is a separate act, with its own permission and its own line in the audit trail.",
        publicationsVersion: "If the content changes the version goes up and any answer the customer had given stops counting. Nobody is left holding an \"approved\" on an old drawing.",
        settingsEnabled: "With the portal switched off nobody gets in: their accounts get the same answer as a wrong password.",
        settingsPublication: "Every kind starts at \"never\". \"On request\" leaves it as a draft for somebody to publish; \"automatic\" shows it as soon as it arrives.",
      },
    },
    modules: {
      "customer-portal": "Customer portal",
    },
    permissions: {
      portal: {
        read: "See the portal: who gets in, what they ask for and what is published",
        publish: "Decide what goes out: publish and withdraw",
        respond: "Answer customers and move their requests along",
        accounts: "Give and take away portal access",
        configure: "Configure the portal: sections, kinds, statuses and roles",
      },
    },
    settings: {
      mail: {
        "customer-portal__portal_invitation": {
          label: "Mailbox portal invitations go out from",
          help: "Empty uses the first mailbox configured. It is the letter a contact chooses their password from.",
        },
        "customer-portal__portal_notice": {
          label: "Mailbox portal notices go out from",
          help: "Empty uses the first mailbox configured. It says there is something waiting in the portal; it never carries the document.",
        },
      },
      ns: {
        "customer-portal": {
          title: "Customer portal",
          subtitle: "How the portal is reached and how long an invitation lasts",
          groups: {
            access: "Access",
          },
          portalUrl: {
            label: "Public address of the portal",
            help: "It is what the invitation link points at. Empty, the link goes nowhere.",
          },
          invitationDays: {
            label: "Days an invitation stays valid",
            help: "An invitation that still works in three months is a key somebody forgot they cut.",
          },
          supportEmail: {
            label: "Portal help address",
            help: "Shown on the sign-in screen. Empty shows nothing.",
          },
        },
      },
    },
    audit: {
      actions: {
        portal_configuration: {
          updated: "Changed the portal configuration",
        },
        portal_role: {
          created: "Created a portal role",
          updated: "Changed a portal role",
          deleted: "Deleted a portal role",
        },
        portal_account: {
          granted: "Gave portal access",
          invited: "Sent the invitation again",
          updated: "Changed a portal account",
          revoked: "Revoked portal access",
          activated: "Activated their portal access",
          password_changed: "Changed their portal password",
        },
        portal_session: {
          started: "Signed in to the portal",
          rejected: "Failed portal sign-in",
        },
        portal_publication: {
          published: "Published something to the portal",
          withdrawn: "Took something off the portal",
          attributed: "Attributed a publication to a customer",
          reassigned: "A publication changed customer at its source and was withdrawn",
        },
        portal_approval: {
          decided: "A customer answered",
          requested: "Asked the customer to approve",
        },
        portal_request: {
          created: "A customer asked for something",
          cancelled: "A customer cancelled their request",
          moved: "Moved a portal request along",
        },
        portal_message: {
          sent: "Wrote to the customer",
          noted: "Left an internal note",
        },
        portal_document: {
          uploaded: "Uploaded a file to the portal",
          downloaded: "A customer downloaded a document",
          removed: "Removed a file from the portal",
        },
      },
    },
    assistant: {
      openers: {
        "customer-portal": {
          "whats-pending": "What are the customers not answering?",
          "how-is-the-portal": "How is the portal doing this month?",
          "answer-a-request": "Answer the last request from…",
          "publish-a-record": "Publish this quote to the customer",
        },
      },
    },
  },
  fr: {
    errors: {
      PORTAL_ROLE_TOO_WIDE: 'Vous ne pouvez pas accorder ni retirer plus de droits que vous n’en avez.',
    },
    portal: {
      title: "Portail client",
      menu: "Menu",
      signOut: "Se déconnecter",
      saved: "Enregistré",
      search: {
        open: "Rechercher",
        title: "Rechercher dans votre portail",
        placeholder: "Référence, machine, chantier, facture…",
        none: "Aucun résultat",
        go: "Rechercher",
        hint: "Tapez deux lettres ou plus pour chercher dans tout ce que vous voyez.",
        keepTyping: "Continuez à taper…",
      },
      sections: {
        home: "Accueil",
        requests: "Demandes",
        approvals: "Approbations",
        projects: "Projets",
        orders: "Commandes",
        assets: "Équipements",
        support: "Assistance",
        maintenance: "Maintenance",
        documents: "Documents",
        quotes: "Devis",
        invoices: "Factures",
        contracts: "Contrats",
        meetings: "Réunions",
        profile: "Mon entreprise",
      },
      requestTypes: {
        support: "Panne ou incident",
        quote: "Demander un devis",
        maintenance: "Demander une maintenance",
        part: "Demander une pièce",
        document: "Demander un document",
        meeting: "Demander une réunion",
      },
      roles: {
        manager: "Responsable",
        operations: "Exploitation",
        accounts: "Administration",
      },
      fields: {
        email: "E-mail",
        password: "Mot de passe",
        newPassword: "Nouveau mot de passe",
        repeatPassword: "Répétez le mot de passe",
        currentPassword: "Mot de passe actuel",
        name: "Nom",
        jobTitle: "Fonction",
        subject: "Objet",
        description: "Dites-nous ce qui se passe",
        site: "Site",
        anySite: "Indifférent",
        about: "À quel sujet",
        nothingInParticular: "Rien en particulier",
        symptom: "Ce que fait la machine",
        since: "Depuis quand",
        stopped: "La ligne est arrêtée",
        need: "Ce dont vous avez besoin",
        deadline: "Pour quand",
        preferred: "Une date qui vous convient",
        part: "Référence ou description",
        quantity: "Quantité",
        attendees: "Qui participera",
        aboutHint: "Ainsi nous savons de quoi il s’agit sans avoir à le demander.",
      },
      signIn: {
        title: "Connectez-vous à votre portail",
        subtitle: "Tout ce qui vous concerne : ce qui est en cours, ce qui attend votre réponse et ce que vous pouvez demander.",
        emailPlaceholder: "votre e-mail",
        go: "Se connecter",
        forgot: "J'ai oublié mon mot de passe",
        forgotTitle: "Récupérer l'accès",
        forgotHelp: "Saisissez votre e-mail et nous vous enverrons un lien pour définir un nouveau mot de passe.",
        forgotGo: "Envoyer le lien",
        forgotSent: "Si cette adresse a un accès, le lien est en route.",
        forgotSentHint: "Si cette adresse a un accès, le lien arrive en quelques minutes et expire.",
      },
      welcome: {
        title: "Choisissez votre mot de passe",
        subtitle: "Dernière étape : vous le choisissez et personne d’autre ne le voit.",
        go: "Se connecter",
        mismatch: "Les deux mots de passe ne correspondent pas",
        noToken: "Ce lien ne contient aucune invitation. Demandez-en un autre à votre fournisseur.",
        passwordHint: "Huit caractères ou plus.",
        noTokenHint: "Ouvrez le lien du courriel tel quel : il est à usage unique et il expire.",
        goToSignIn: "Aller à la connexion",
      },
      home: {
        pending: "En attente de votre part",
        nothingPending: "Rien n’attend votre réponse",
        inProgress: "En cours",
        upcoming: "À venir",
        activity: "Dernières nouveautés",
        ask: "De quoi avez-vous besoin ?",
        summarise: "Résumez la situation",
        goHome: "Revenir à l’accueil",
        greeting: "Bonjour, {name}",
        greetingAnon: "Bonjour",
        nothingPendingHint: "Rien n’attend votre réponse.",
        summaryTitle: "Où en sont les choses",
        brandNew: "Il n’y a encore rien ici",
        brandNewHint: "Dès qu’il y aura quelque chose à vous montrer, cela apparaîtra sur cet écran. Entre-temps, vous pouvez nous écrire d’ici.",
      },
      activity: {
        published: "Nouveau : {what}",
        replied: "{what} a répondu",
        wrote: "{what} a écrit",
      },
      card: {
        waitingOnYou: "Attend votre réponse",
        on: "{date}",
        due: "Pour le {date}",
      },
      decisionState: {
        pending: "En attente",
        approved: "Approuvé",
        rejected: "Refusé",
        changes: "Modifications demandées",
        superseded: "Ne compte plus : une nouvelle version existe",
        cancelled: "Annulé",
      },
      section: {
        ask: "Faire une demande",
        filter: "Rechercher ici",
        none: "Rien encore dans {what}",
        noneFiltered: "Aucun résultat",
        clear: "Effacer le filtre",
        count: "1 élément | {n} éléments",
        noneHint: "Quand il y aura quelque chose à vous montrer dans cette section, cela apparaîtra ici.",
        noneFilteredHint: "Essayez d’autres mots.",
      },
      pager: {
        previous: "Précédent",
        next: "Suivant",
        of: "Page {page} sur {pages}",
      },
      item: {
        back: "Retour",
        status: "Statut",
        amount: "Montant",
        date: "Date",
        dueDate: "Date prévue",
        decisionAsked: "Ceci attend votre réponse (version {version})",
        commentPlaceholder: "Dites-nous pourquoi, si vous le souhaitez",
        approve: "Approuver",
        changes: "Demander des modifications",
        reject: "Refuser",
        decisionSent: "Envoyé. Ils l’ont reçu.",
        explain: "Expliquez-le en deux lignes",
        description: "Désignation",
        quantity: "Quantité",
        unitPrice: "Prix unitaire",
        total: "Total",
        files: "Documents",
        decisions: "Réponses données",
        versionIs: "· version {version}",
        byWho: "· {who}",
        version: "Version",
        lines: "Détail",
        progress: "Avancement",
        progressIs: "{percent}% fait",
        comment: "Commentaire",
        commentHint: "Facultatif, et lu par celui qui vous l’a envoyé.",
        decidedOn: "Répondu le {date}",
      },
      thread: {
        title: "Conversation",
        empty: "Personne n’a encore écrit",
        placeholder: "Écrivez ici",
        send: "Envoyer",
        attach: "Joindre",
        chosen: "{count} fichier(s) prêts à envoyer",
        removeFile: "Retirer le fichier",
      },
      requests: {
        new: "Nouvelle demande",
        newTitle: "De quoi avez-vous besoin ?",
        none: "Vous n’avez encore rien demandé",
        whatKind: "De quoi s’agit-il ?",
        sayIt: "Dites-le comme vous voulez",
        sayItPlaceholder: "La machine fait un bruit étrange quand l’axe monte…",
        tidyUp: "Mettez-le au propre",
        stillMissing: "Ceci les aiderait :",
        send: "Envoyer la demande",
        sent: "Envoyée. Sa référence est {code}.",
        tabs: {
          open: "Ouvertes",
          all: "Toutes",
        },
        newSubtitle: "Dites-nous ce dont vous avez besoin ; la personne concernée vous répondra ici.",
        details: "Les détails",
        noneOpen: "Vous n’avez rien en cours",
        noneHint: "Tout ce que vous demandez ici est enregistré, avec son numéro et sa réponse.",
        noTypes: "Rien ne peut être demandé pour l’instant",
        noTypesHint: "Parlez-en à votre interlocuteur habituel.",
        leaveTitle: "Laisser ceci à moitié écrit ?",
        leaveBody: "Ce que vous avez écrit sera perdu.",
      },
      requestStatus: {
        open: "Reçue",
        in_progress: "En cours",
        waiting_customer: "En attente de votre part",
        resolved: "Résolue",
        closed: "Clôturée",
        cancelled: "Annulée",
      },
      request: {
        kind: "Type",
        filed: "Envoyée le",
        about: "Concerne",
        cancel: "Annuler la demande",
        cancelTitle: "Annuler cette demande ?",
        cancelBody: "Elle ne peut être annulée que si personne ne l’a commencée.",
        cancelled: "Annulée",
        waitingOnYou: "Ils attendent votre réponse",
        waitingOnYouHint: "Ils ont posé une question et ont besoin de votre réponse pour avancer.",
        closed: "Clôturée le",
      },
      profile: {
        company: "Votre entreprise",
        name: "Nom",
        code: "Code",
        sites: "Sites",
        me: "Vos coordonnées",
        emailNotices: "Me prévenir par e-mail",
        password: "Mot de passe",
        changePassword: "Changer le mot de passe",
        passwordChanged: "C’est fait. Vos autres sessions ont été fermées.",
        colleagues: "Qui d’autre y accède",
        remove: "Retirer l’accès",
        removeTitle: "Retirer l’accès à {who} ?",
        removeBody: "Cette personne ne pourra plus se connecter. Ce qu’elle a déjà demandé ou signé reste.",
        invite: "Inviter quelqu’un",
        invited: "Invitation envoyée",
        subtitle: "Vos coordonnées, votre mot de passe et qui d’autre de votre entreprise se connecte ici.",
        everySite: "Vous voyez tous ceux de votre entreprise.",
        emailNoticesHint: "Un courriel quand quelque chose attend votre réponse.",
        passwordHint: "La changer déconnecte vos autres appareils.",
        you: "Vous",
        inviteTitle: "Donner accès à quelqu’un d’autre",
        removed: "Accès retiré",
      },
      skipToContent: "Aller au contenu",
    },
    customerPortal: {
      title: "Portail client",
      saved: "Enregistré",
      subtitle: "Ce que vos clients consultent, demandent et approuvent eux-mêmes",
      nav: {
        inbox: "Demandes",
        publications: "Publications",
        accounts: "Accès",
        settings: "Configuration du portail",
      },
      actions: {
        configure: "Configurer",
        revoke: "Retirer l’accès",
      },
      confirm: {
        revoke: "Retirer l’accès au portail à cette personne ?",
      },
      overview: {
        off: "Le portail est désactivé : personne de l’extérieur ne peut encore y accéder.",
        turnOn: "L’activer",
        openRequests: "Demandes ouvertes",
        awaiting: "En attente du client",
        customers: "Clients avec accès",
        accounts: "Personnes avec accès",
        published: "Ce qui est publié",
        nothingPublished: "Rien n’a encore été publié",
        kind: "Type",
        visible: "Publié",
        draft: "Non publié",
        offBadge: "Éteint",
        seeAll: "Tout voir",
        nothingPublishedHint: "Dès que vous publiez quelque chose, cela apparaît ici, compté par type.",
        noRequests: "Personne n’a encore rien demandé",
        noRequestsHint: "Ce que vos clients demandent dans leur portail arrive dans la boîte.",
      },
      kinds: {
        project: "Projets",
        order: "Commandes",
        asset: "Équipements",
        document: "Documents",
        quote: "Devis",
        invoice: "Factures",
        contract: "Contrats",
        ticket: "Incidents",
        maintenance: "Maintenances",
        meeting: "Réunions",
      },
      visibility: {
        internal: "Interne seulement",
        customer: "Le client le voit",
      },
      accountStatus: {
        invited: "Invité",
        active: "Actif",
        suspended: "Suspendu",
        revoked: "Retiré",
      },
      requestStatus: {
        open: "Reçue",
        in_progress: "En cours",
        waiting_customer: "En attente du client",
        resolved: "Résolue",
        closed: "Clôturée",
        cancelled: "Annulée",
      },
      accounts: {
        title: "Qui accède au portail",
        subtitle: "Chaque accès est une clé vers les données d’une entreprise cliente",
        invite: "Donner un accès",
        search: "Personne, e-mail ou client",
        none: "Personne n’a encore d’accès",
        customer: "Client",
        role: "Rôle dans le portail",
        roleHint: "Détermine les sections visibles et les actions permises.",
        sites: "Limiter à ces sites",
        sitesHint: "Si aucun n’est coché, la personne voit tous les sites de son entreprise.",
        send: "Envoyer l’invitation",
        invited: "Invitation envoyée",
        status: "Statut",
        allStatuses: "Tous",
        lastSeen: "Dernière connexion",
        never: "Jamais connecté",
        resend: "Renvoyer l’invitation",
        resent: "Invitation renvoyée",
        suspend: "Suspendre",
        revoke: "Retirer l’accès",
        revokeTitle: "Retirer l’accès à {who} ?",
        revokeBody: "Ses sessions se ferment immédiatement. Ce qu’elle a demandé et signé reste à son nom.",
        searchPlaceholder: "Nom, adresse ou entreprise",
        noneFiltered: "Aucun compte avec ce filtre",
        noneFilteredHint: "Essayez un autre nom, une autre adresse ou un autre statut.",
        noneHint: "Invitez le premier contact d’un client : il recevra un courriel avec un lien pour définir son mot de passe.",
        openDetail: "Voir la fiche",
        reinstate: "Réactiver",
        reinstated: "Compte réactivé",
        suspended: "Compte suspendu",
        revoked: "Accès retiré",
        everySite: "Tous les leurs",
        someSites: "Seulement 1 site | Seulement {n} sites",
      },
      publications: {
        title: "Ce que voit chaque client",
        subtitle: "Rien ne sort tant que personne ne l’a publié",
        search: "Référence, titre ou client",
        none: "Il n’y a encore rien à publier",
        reference: "Référence",
        what: "De quoi il s’agit",
        customer: "Client",
        unattributed: "Non attribué",
        visibility: "Visibilité",
        allKinds: "Tous les types",
        allVisibility: "Publié ou non",
        draftOnly: "Non publiés seulement",
        publishedOnly: "Publiés seulement",
        internalStatus: "Statut interne",
        externalStatus: "Ce que lit le client",
        version: "Version",
        whose: "À quel client appartient-il ?",
        decisions: "Réponses du client",
        decisionLine: "Version {version} : {state} {who}",
        attribute: "Attribuer à ce client",
        askDecision: "Lui demander de l’approuver",
        decisionAsked: "C’est maintenant en attente chez le client",
        withdraw: "Retirer du portail",
        withdrawTitle: "Retirer du portail ?",
        withdrawBody: "Le client cesse de le voir. S’il l’a déjà vu, mieux vaut le prévenir.",
        publish: "Publier",
        publishTitle: "Le publier pour {who} ?",
        publishBody: "Ils le verront désormais dans leur portail.",
        published: "Publié",
        searchPlaceholder: "Référence, titre ou client",
        noneFiltered: "Rien avec ce filtre",
        noneFilteredHint: "Essayez une autre référence, un autre type ou un autre état de publication.",
        noneHint: "Ce que les autres modules annoncent apparaît ici. Vide signifie soit rien à annoncer, soit ce type réglé sur « jamais » dans les réglages.",
        versionShort: "v{version}",
        awaitingDecision: "En attente de réponse",
        untranslated: "Pas encore traduit",
        whoseHint: "Impossible de publier sans client : ce serait montré au premier compte qui le demande.",
        withdrawn: "Retiré du portail",
      },
      inbox: {
        title: "Ce que demandent les clients",
        subtitle: "Tout ce qui arrive par le portail, au même endroit",
        search: "Référence, objet ou client",
        none: "Aucune demande",
        code: "Référence",
        subject: "Objet",
        kind: "Type",
        when: "Quand",
        openOnly: "Ouvertes seulement",
        all: "Toutes",
        filedBy: "Qui l’a envoyée",
        moveItOn: "Faire avancer",
        assignee: "Responsable",
        nobody: "Personne",
        conversation: "Conversation",
        internalNote: "Note interne",
        replyPlaceholder: "Rédigez la réponse",
        sendToCustomer: "Envoyer au client",
        noteInternally: "Enregistrer comme note interne",
        searchPlaceholder: "Code, objet ou client",
        noneFiltered: "Aucune demande avec ce filtre",
        noneFilteredHint: "Essayez d’autres mots, ou changez le statut.",
        noneHint: "Ce que vos clients demandent dans leur portail apparaîtra ici.",
        nothingSaid: "Rien n’a encore été écrit.",
        reply: "Réponse",
        replySent: "Envoyé au client",
        noteSaved: "Note enregistrée, visible seulement par vous",
      },
      settings: {
        title: "Configuration du portail",
        subtitle: "Quelles sections existent, comment elles s’appellent et ce qui sort",
        enabled: "Portail activé",
        enabledHelp: "Désactivé, personne de l’extérieur ne peut entrer, même avec un mot de passe.",
        welcome: "Phrase d’accueil",
        welcomeHint: "Elle apparaît en haut de leur page d’accueil.",
        footer: "Pied de page",
        brandName: "Nom vu par le client",
        brandNameHint: "Vide, le nom de l’entreprise est utilisé.",
        logo: "Adresse du logo",
        sectionsHelp: "Activez, renommez et ordonnez. Une section alimentée par un module non installé n’est pas affichée, même activée.",
        rename: "L’appeler autrement",
        up: "Monter",
        down: "Descendre",
        fedBy: "Alimentée par {module}",
        ownSection: "Propre au portail, ne dépend de rien",
        publicationHelp: "Ce qui sort vers le portail, et quand. Tout commence à « jamais » : installer le module ne publie rien.",
        statesHelp: "Ce que dit l’ERP, et ce que lit le client. La colonne de gauche se remplit avec les statuts que portent réellement les enregistrements ; ceux que vous avez déjà traduits restent, même si plus rien ne les porte.",
        statesEmpty: "Aucun statut à traduire n’est encore arrivé.",
        seenTimes: "1 fois | {n} fois",
        requestTypesHelp: "Les types de demande vous appartiennent : ajoutez, retirez, renommez.",
        newType: "Nom du nouveau type",
        addType: "Ajouter",
        rolesHelp: "Les rôles du portail sont externes : ce ne sont pas des rôles ERP et ils n’ouvrent aucun écran ici.",
        readOf: "Voir {section}",
        roleSaved: "Enregistré. Cela concerne {count} personnes.",
        aiHelp: "Chacune consomme le budget IA de l’instance.",
        aiDraft: "Mettre au propre ce que le client écrit",
        aiSummary: "Résumer l’état de la relation",
        aiItem: "Expliquer une fiche en deux lignes",
        tabs: {
          general: "Général",
          sections: "Sections",
          publication: "Ce qui est publié",
          states: "Comment on le dit",
          requests: "Demandes",
          roles: "Rôles",
          ai: "IA",
        },
        footerHint: "Le pied de page de chaque écran du portail. En général un téléphone ou une adresse.",
        logoHint: "L’adresse d’une image. Elle remplace le nom dans la barre du haut.",
        rule: "Quand c’est visible",
        sayItAs: "Comment on le dit dehors",
        statesEmptyHint: "Cette liste se remplit toute seule avec les statuts que portent les événements. Aucun n’est encore arrivé.",
        noFields: "Aucun champ propre",
        noTypes: "Aucun type de demande",
        noTypesHint: "Sans types, vos clients ne peuvent rien demander. Ajoutez-en au moins un.",
        newTypeHint: "Le nom que verra le client : « Panne », « Pièce », « Question technique ».",
        removeTypeTitle: "Retirer « {what} » ?",
        removeTypeBody: "Il cesse d’être proposé aux clients. Les demandes déjà déposées sous ce type restent telles quelles.",
        saveRole: "Enregistrer le rôle",
        notSeenYet: "Aucun n’est encore arrivé",
      },
      rules: {
        never: "Jamais",
        on_request: "Quand quelqu’un le publie",
        automatic: "Automatiquement",
      },
      rulesHelp: {
        never: "Rien de ce type n’est conservé ni montré.",
        on_request: "Conservé sans publication ; quelqu’un décide lesquels sortent.",
        automatic: "Sort automatiquement dès son arrivée.",
      },
      capabilities: {
        approve: "Approuver et refuser",
        request: "Faire des demandes",
        upload: "Envoyer des fichiers",
        comment: "Écrire dans la conversation",
        confirm: "Confirmer livraisons et dates",
        dispute: "Contester",
        download: "Télécharger des documents",
        manage_users: "Donner accès à ses collègues",
      },
      related: {
        accounts: "Portail",
        requests: "Demandes du portail",
      },
      dashboard: {
        openRequests: "Demandes ouvertes",
        openRequestsHelp: "Combien de demandes des clients via le portail sont encore ouvertes.",
        awaitingCustomer: "En attente du client",
        awaitingCustomerHelp: "Combien d’éléments publiés attendent encore une réponse du client : plans, devis, dates.",
        latestRequests: "Les dernières demandes",
        latestRequestsHelp: "Les demandes ouvertes les plus récentes, avec le client qui les a envoyées.",
      },
      notifications: {
        categories: {
          request: "Demandes du portail",
          decision: "Réponses du client",
          message: "Messages du portail",
        },
        request: {
          title: "Nouvelle demande sur le portail",
          body: "{customer} : {subject}",
        },
        decision: {
          approved: "Un client a approuvé",
          rejected: "Un client a refusé",
          changes: "Un client demande des modifications",
          body: "{customer} : {reference}",
        },
        message: {
          title: "Message sur le portail",
          body: "{author} : {subject}",
        },
      },
      priorities: {
        low: "Basse",
        normal: "Normale",
        high: "Haute",
        urgent: "Urgente",
      },
      guide: {
        overviewStats: "Quatre chiffres : ce qu’ils ont demandé, ce qu’ils attendent de vous, combien de clients ont un portail et combien de personnes s’y connectent.",
        overviewPublished: "Chaque type d’enregistrement, avec ce que le client voit déjà et ce qui reste en brouillon. Beaucoup de brouillons et rien de publié, c’est un portail vide vu de l’extérieur.",
        publicationsDraft: "Tout ce qui arrive des autres modules atterrit ici en brouillon et reste invisible de l’extérieur. Publier est un acte à part, avec sa permission et sa trace dans l’audit.",
        publicationsVersion: "Si le contenu change, la version augmente et la réponse déjà donnée par le client cesse de compter. Personne ne garde un « approuvé » sur un ancien plan.",
        settingsEnabled: "Portail éteint, personne n’entre : leurs comptes reçoivent la même réponse qu’avec un mot de passe erroné.",
        settingsPublication: "Chaque type démarre à « jamais ». « Sur demande » le laisse en brouillon pour que quelqu’un le publie ; « automatique » l’affiche dès son arrivée.",
      },
    },
    modules: {
      "customer-portal": "Portail client",
    },
    permissions: {
      portal: {
        read: "Voir le portail : qui y accède, ce qui est demandé et ce qui est publié",
        publish: "Décider ce qui sort : publier et retirer",
        respond: "Répondre aux clients et faire avancer leurs demandes",
        accounts: "Donner et retirer l’accès au portail",
        configure: "Configurer le portail : sections, types, statuts et rôles",
      },
    },
    settings: {
      mail: {
        "customer-portal__portal_invitation": {
          label: "Boite d’envoi des invitations au portail",
          help: "Vide, la premiere boite configuree est utilisee. C’est la lettre par laquelle un contact choisit son mot de passe.",
        },
        "customer-portal__portal_notice": {
          label: "Boite d’envoi des avis du portail",
          help: "Vide, la premiere boite configuree est utilisee. Il annonce qu’un element attend dans le portail, sans jamais joindre le document.",
        },
      },
      ns: {
        "customer-portal": {
          title: "Portail client",
          subtitle: "Comment accéder au portail et durée d’une invitation",
          groups: {
            access: "Accès",
          },
          portalUrl: {
            label: "Adresse publique du portail",
            help: "C’est la cible du lien d’invitation. Vide, le lien ne mène nulle part.",
          },
          invitationDays: {
            label: "Jours de validité d’une invitation",
            help: "Une invitation encore valable dans trois mois est une clé oubliée.",
          },
          supportEmail: {
            label: "Adresse d’aide du portail",
            help: "Affichée sur l’écran de connexion. Vide, rien ne s’affiche.",
          },
        },
      },
    },
    audit: {
      actions: {
        portal_configuration: {
          updated: "A modifié la configuration du portail",
        },
        portal_role: {
          created: "A créé un rôle de portail",
          updated: "A modifié un rôle de portail",
          deleted: "A supprimé un rôle de portail",
        },
        portal_account: {
          granted: "A donné un accès au portail",
          invited: "A renvoyé l’invitation",
          updated: "A modifié un accès au portail",
          revoked: "A retiré l’accès au portail",
          activated: "A activé son accès au portail",
          password_changed: "A changé son mot de passe du portail",
        },
        portal_session: {
          started: "S’est connecté au portail",
          rejected: "Échec de connexion au portail",
        },
        portal_publication: {
          published: "A publié un élément sur le portail",
          withdrawn: "A retiré un élément du portail",
          attributed: "A attribué une publication à un client",
          reassigned: "Une publication a changé de client à la source et a été retirée",
        },
        portal_approval: {
          decided: "Un client a répondu",
          requested: "A demandé une approbation au client",
        },
        portal_request: {
          created: "Un client a fait une demande",
          cancelled: "Un client a annulé sa demande",
          moved: "A fait avancer une demande du portail",
        },
        portal_message: {
          sent: "A écrit au client",
          noted: "A laissé une note interne",
        },
        portal_document: {
          uploaded: "A envoyé un fichier sur le portail",
          downloaded: "Un client a téléchargé un document",
          removed: "A supprimé un fichier du portail",
        },
      },
    },
    assistant: {
      openers: {
        "customer-portal": {
          "whats-pending": "À quoi les clients ne répondent-ils pas ?",
          "how-is-the-portal": "Comment va le portail ce mois-ci ?",
          "answer-a-request": "Réponds à la dernière demande de…",
          "publish-a-record": "Publie ce devis pour le client",
        },
      },
    },
  },
}

// The assistant cards (customerPortal.assistant.*), from their own file.
for (const [locale, block] of Object.entries(assistantMessages)) {
  messages[locale].customerPortal.assistant = { ...messages[locale].customerPortal.assistant, ...block }
}

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

Object.assign(messages, withToolLabels(messages))
